import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom'
import { useLocale } from '../i18n/context'
import { AnimatePresence, motion } from 'framer-motion'
import { Copy, Layers, MoreHorizontal, Plus, SquarePen } from 'lucide-react'
import { useDeck, useDecks, useCreateDeck, useDeleteDeck, useUpdateDeck, useToggleDeckLearning, isDeckOwner, exportDeckCsv } from '../hooks/useDecks'
import { useCopyDeck } from '../hooks/usePublic'
import { extractErrorMessage } from '../api/client'
import { useHistorySentinel } from '../hooks/useHistorySentinel'
import { useScrollportMetrics } from '../hooks/useScrollportMetrics'
import { deckScreenFrame } from '../utils/deckPaneViewport'
import { useScreenHeader } from '../hooks/useScreenHeader'
import type { ScreenHeaderConfig } from '../stores/screenHeaderStore'
import { useCardsInfinite, useCardCounts, useCardSearchInfinite, useDeleteCard, useDeleteCards, useRestoreCards, useMoveCard, useRestoreCardOrder, useMoveCards, useCopyCards, useUndoCardCopy, useLocateCard } from '../hooks/useCards'
import type { DeckSearchStatus } from '../components/DeckSearchPanel'
import {
  EMPTY_SELECTION, addCardsSelected, isCardSelected, isSelectionActive,
  selectWholeDeck, selectionCount, selectionFilterChanged, setCardSelected, toSelectionRequest,
  toggleCardSelected,
  type CardSelectionRequest, type DeckSelection,
} from '../utils/deckSelection'
import { relativePlacement } from '../utils/cardOrder'
import { filterCards, type CardFilterChip } from '../utils/cardFilters'
import { useFillAllFlow, useFillSelectionFlow } from '../hooks/useDeckFillFlows'
import DeckFilterChipsBar from '../components/DeckFilterChipsBar'
import FillAllSheet from '../components/FillAllSheet'
import FillAllProgressBar from '../components/FillAllProgressBar'
import CSVImportModal from '../components/shared/CSVImportModal'
import WordReferencePanel from '../components/learning/WordReferencePanel'
import type { Card, CardSearchItem, Deck } from '../types'
import { useAuthStore, selectUser } from '../stores/authStore'
import { shareLink } from '../utils/shareLink'
import { buildCardIndexMap } from '../utils/deckCardNavigation'
import LearningToggleSwitch from '../components/shared/LearningToggleSwitch'
import { DeckDailySwitch, DeckDetailsDialog, HeaderCapsule } from '../components/DeckHeaderControls'
import { DeckLanguageField } from '../components/DeckLanguageField'
import { getLangMeta } from '../utils/languages'
import DeckCover from '../components/shared/DeckCover'
import { DeckResetConfirm } from '../components/shared/MasteryResetConfirm'
import CEFRBadge from '../components/shared/CEFRBadge'
import DeckDesktopPanels, { type DeckDesktopPanelsHandle } from '../components/DeckDesktopPanels'
import { resolveDeckEscapeRung } from '../utils/deckEscapeLadder'
import DeckMobileFeed from '../components/DeckMobileFeed'
import { DeckAnchorNotice } from '../components/DeckAnchorNotice'
import DeckReorderScreen from '../components/DeckReorderScreen'
import DeckTopStrip, { DECK_TOP_STRIP_CLEARANCE } from '../components/DeckTopStrip'
// The deck's «Карточки | Тексты» (handoff §8, §19.6; ADR-115 Decision 11).
import DeckSegmentSwitch from '../components/texts/DeckSegmentSwitch'
import DeckTextsMobile from '../components/texts/DeckTextsMobile'
import DeckTextsPanels from '../components/texts/DeckTextsPanels'
import { useAddTextToDeck } from '../hooks/useAddTextToDeck'
import { useDeckSegment, type DeckSegment } from '../stores/deckSegment'
import { getAppScrollElement } from '../utils/appScroll'
import { createDeckRowRegistry, type DeckRowRegistry } from '../utils/deckRowRegistry'
import { createScrollMotion, PLACEMENT_DURATION, type ScrollMotion } from '../utils/deckFocusMotion'
import { libraryHref } from '../utils/libraryAddress'

const PAGE_SIZE = 30

/**
 * The column-head line's height (DESIGN_SYSTEM.md §13's "36 head + 12"): the
 * line the pane's top edge, the card-search field and `Add card` all start on.
 *
 * One constant for all three controls on it, because the claim §13 makes is
 * that they share a line — three independently-sized controls that happen to
 * come out level would stop being level the moment any one of their paddings
 * moved. The 12px gap below it to the list's first row lives in
 * `DeckDesktopPanels`, with the head it belongs to.
 */
const DECK_COLUMN_HEAD_H = 36

type UndoPending =
  // `originalOrder` names each moved card's immediate predecessor in the
  // SOURCE deck at the moment of the move (`null` for "was first") — a
  // relative-move rollback, not the absolute positions the removed
  // `/cards/reorder` endpoint used to take. See `useRestoreCardOrder`.
  | { mode: 'move'; count: number; destDeckId: string; destDeckTitle: string; cardIds: string[]; sourceDeckId: string; originalOrder: { id: string; afterId: string | null }[] }
  // A bulk copy. `undoToken` names the server's record of what THAT copy
  // created. There is deliberately no `cardIds` here: a copy's card ids used to
  // be derived from (source card, target deck) and were therefore identical for
  // every copy of those cards into that deck, so undoing one copy deleted
  // whichever copy existed — including one an earlier, unrelated operation had
  // made. The token belongs to one operation and is spent on first use.
  | { mode: 'copy'; count: number; destDeckId: string; destDeckTitle: string; undoToken: string }
  // A bulk delete. `restoreToken` names the server's snapshot of what was
  // removed — the cards with their sides, their positions and their learning
  // state. There is deliberately no `cardIds` here: the ids alone would name
  // nothing restorable, and on a deck-scoped delete this page never held most
  // of them anyway.
  // `count` is what was actually deleted and `requested` what was asked for;
  // they are carried separately so the offer states the partial rather than
  // rounding it up to the number the reader had selected.
  | { mode: 'delete'; count: number; requested: number; deckId: string; restoreToken: string }

export default function DeckDetailPage() {
  const { tr } = useLocale()
  const dd = tr.deckDetail
  const dm = tr.deckManagement
  const dr = tr.deckReorder
  // Shared with the phone: a field deleted and an authority handover read the
  // same on both surfaces because they are the same events, reported by the
  // same component (ActiveVocabCard).
  const cb = tr.cardBubble
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  // Capture the return URL once at mount — stable for the lifetime of this page view.
  const fromUrl: string | undefined = (location.state as { from?: string } | null)?.from

  // ── ALL hooks must be called unconditionally, before any return ────────────
  // The full-screen single-card editor's state used to open here — which card
  // it was on, and a SNAPSHOT of the filtered feed for its swipe to walk. Both
  // went with it (ADR-067 Decision F). The conveyor they served is not lost:
  // the feed's own `‹ 42 OF 150 ›` arrows walk the live chip-filtered set, and
  // answer the vanishing-card hazard the snapshot was taken for by
  // deactivating a card that leaves the shown set rather than pretending it is
  // still there.
  //
  // Prompt 8 management chrome triggers — the trigger buttons live in the
  // shared deck overflow menu below (mobile-only items), above
  // DeckMobileFeed in the tree, so a boolean has to cross that boundary.
  // The chrome's own detail state (search query, filter chip, Fill-all
  // preflight/progress) stays local to DeckMobileFeed.
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  // Closing search is handed down with a stable identity: the feed's deep-link
  // arrival clears an open search to reach the card it was sent to, so this
  // reaches an effect's dependency list rather than only a click handler.
  const closeMobileSearch = useCallback(() => setMobileSearchOpen(false), [])
  // The scroll position to come back to when search closes, captured at the
  // moment search OPENS — the only point that still precedes the header
  // collapsing below, so it is the true pre-search position rather than
  // whatever the scroller has already settled to once the header is gone.
  // A ref, not state: DeckMobileFeed reads it imperatively from its own
  // effect, on the closing edge of `searchOpen`, and nothing here should
  // re-render on account of it. `null` means "nothing to restore" — the
  // initial value, and what it's reset to once a restore has consumed it.
  const preSearchScrollTopRef = useRef<number | null>(null)
  // The filter chips' own affordance (brief §16), independent of search since
  // the 2026-08-04 rework — opened from the ⋯ menu, unaffected by search's
  // move to the top strip's own control.
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false)
  // The desktop halves of the same management layer (brief §23). The mobile
  // triggers above summon `DeckMobileFeed`'s chrome; these summon the very same
  // components rendered in the desktop branch below — `DeckFilterChipsBar`,
  // `FillAllSheet` and `DeckReorderScreen`, none of them re-implemented — so
  // the MANAGE group in the ⋯ menu can be one group for both surfaces rather
  // than a mobile-only section beside a desktop-only one.
  const [desktopFilterOpen, setDesktopFilterOpen] = useState(false)
  const [desktopChip, setDesktopChip] = useState<CardFilterChip>('all')
  const [desktopFillAllOpen, setDesktopFillAllOpen] = useState(false)
  // The mobile Reorder screen (brief §4.4) and the feed's chip state it has to
  // refuse on. The chip itself lives in DeckMobileFeed; only "is one on" needs
  // to reach the ⋯ menu here.
  const [reorderScreenOpen, setReorderScreenOpen] = useState(false)
  const [mobileFilterActive, setMobileFilterActive] = useState(false)
  const [mobileFillAllOpen, setMobileFillAllOpen] = useState(false)
  const [confirmDeleteDeck, setConfirmDeleteDeck] = useState(false)
  // ADR-098 deck reset confirm — open for any reader with study history here,
  // owner or not: the progress it resets is the reader's own.
  const [confirmResetDeck, setConfirmResetDeck] = useState(false)
  const [confirmDeleteCardId, setConfirmDeleteCardId] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [toastMsg, setToastMsg] = useState<string | null>(null)
  /**
   * What the bulk actions act on (ADR-067 Decision 6, brief §23 item 9).
   *
   * Was a `Set<string>` of the ids this page had paged in, which is what made
   * `Select all` a lie: the counter printed the loaded count as the deck's and
   * Delete and Move then acted on that subset. A whole-deck selection now
   * carries no ids — see `utils/deckSelection`.
   */
  const [selection, setSelection] = useState<DeckSelection>(EMPTY_SELECTION)
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [transferMode, setTransferMode] = useState<'move' | 'copy'>('move')
  const [editingTitle, setEditingTitle] = useState<string | null>(null)
  const [editingDescription, setEditingDescription] = useState<string | null>(null)
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [referenceCardId, setReferenceCardId] = useState<string | null>(null)
  const [referenceTerm, setReferenceTerm] = useState('')
  // The reference panel's two transitions. Declared up here, above the early
  // returns, because the desktop panels depend on their identity (they follow
  // the selection while the panel is open). Sets, never toggles: the reading
  // pane's control decides open or closed, and following the selection must
  // never close the panel by accident.
  const handleShowReference = useCallback((cardId: string, term: string) => {
    setReferenceCardId(cardId)
    setReferenceTerm(term)
  }, [])
  const closeReferencePanel = useCallback(() => {
    setReferenceCardId(null)
    setReferenceTerm('')
  }, [])
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768)
  // See the effect below — the two-pane threshold, independent of `isDesktop`.
  const [narrowPanels, setNarrowPanels] = useState(() => window.innerWidth < 1040)
  /**
   * Desktop `+ Card` — a request for a new card, counted rather than flagged
   * so two presses are two cards (ADR-067 Decision F: creating a card uses the
   * right panel, the same surface that edits one).
   *
   * It lives here rather than in `DeckDesktopPanels` because the button does:
   * `+ Card` sits beside the search box, above the panels in the tree, and the
   * empty deck's `Add first card` is above them too — and is what mounts them
   * at all. The panels watch the number and open their own draft.
   */
  const [newCardNonce, setNewCardNonce] = useState(0)
  const requestNewCard = useCallback(() => setNewCardNonce((n) => n + 1), [])
  /**
   * The deck-level editing mode (ADR-067 amendment, 2026-09-08): while it is
   * on, every card the reader opens in the right pane opens into editing
   * rather than into reading.
   *
   * It lives on the page rather than inside `DeckDesktopPanels` because it
   * belongs to the DECK, not to a card: it is entered from the deck's overflow
   * menu, and both its indicator and its off switch are published into the
   * screen-header bar beside the deck's name (see the useScreenHeader call
   * below) so they stay visible — and reachable — with no card selected at
   * all. Desktop only; the phone has its own modes and this is not one of
   * them.
   */
  const [deckEditMode, setDeckEditMode] = useState(false)
  const [undoPending, setUndoPending] = useState<UndoPending | null>(null)
  const [undoError, setUndoError] = useState(false)
  const [undoWorking, setUndoWorking] = useState(false)
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * Field-level transients from the desktop right panel: the Undo offered when
   * a FILLED field is deleted (§12), and the sentence ADR-066 Decision 1 asks
   * for when a reorder handed a repeated kind's authority to another field.
   *
   * Deliberately not folded into `undoPending` above. That one carries bulk
   * operations whose Undo is a server-issued token, survives a remount and can
   * be retried; this Undo is a closure into the active card's own draft and
   * stops meaning anything the moment another card is opened — which is exactly
   * why it is cleared when the panel's selection changes rather than only on a
   * timer. The order notice carries no Undo at all, and the omission is the same
   * one the phone makes: the way back is to drag the field back, which is the
   * gesture just made and stays available for as long as the card is open, not
   * for the few seconds a button would live.
   */
  const [fieldTransient, setFieldTransient] = useState<
    | { kind: 'undo'; label: string; restore: () => void }
    | { kind: 'notice'; message: string }
    | null
  >(null)
  const fieldTransientTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const showFieldTransient = useCallback(
    (next: NonNullable<typeof fieldTransient>, ms: number) => {
      if (fieldTransientTimerRef.current) clearTimeout(fieldTransientTimerRef.current)
      setFieldTransient(next)
      fieldTransientTimerRef.current = setTimeout(() => {
        fieldTransientTimerRef.current = null
        setFieldTransient(null)
      }, ms)
    },
    [],
  )
  const clearFieldTransient = useCallback(() => {
    if (fieldTransientTimerRef.current) clearTimeout(fieldTransientTimerRef.current)
    fieldTransientTimerRef.current = null
    setFieldTransient(null)
  }, [])
  useEffect(() => clearFieldTransient, [clearFieldTransient])
  // Shift-range anchor: the last card that was plain-toggled (by id, not index,
  // so it survives drag-reorders). Used to compute the shift-click range.
  const [anchorCardId, setAnchorCardId] = useState<string | null>(null)
  // Card list search + per-card overflow state
  const [cardSearch, setCardSearch] = useState('')
  // Proofreading mode — every card row is directly editable with one tap.
  const [proofreadMode, setProofreadMode] = useState(false)
  // The single "leave editing mode" action — Done's button, the mobile edit
  // bar's Done, and (below) the Android back sentinel all call this same
  // function, so leaving by back is never distinguishable from leaving by
  // Done: same fields cleared, nothing extra torn down.
  const exitEditingMode = useCallback(() => {
    setProofreadMode(false)
  }, [])
  // Paint-selection gesture: set on checkbox mousedown, cleared on pointerup.
  const isPaintingRef = useRef(false)
  const paintModeRef = useRef<'select' | 'deselect'>('select')
  // Suppresses the synthetic click that fires after a pointerdown-handled checkbox,
  // so the card doesn't get double-toggled.
  const suppressCheckboxClickRef = useRef(false)

  // ── Touch paint-selection (mobile) ────────────────────────────────────────
  // Container ref so we can attach { passive: false } touch listeners that can
  // call preventDefault() to block scroll while painting.
  const cardListRef = useRef<HTMLDivElement>(null)
  /**
   * The desktop screen's frame. `scrollport` is the app scrollport's client
   * height and padding, measured rather than written down; the frame runs from
   * the scrollport's top edge to its bottom edge (`deckScreenFrame` says why
   * edges and not the content box). There is no band to measure any more: the
   * deck-scoped chrome that shared the frame with the columns moved into the
   * screen-header bar (ADR-067, thirteenth amendment), so the columns get the
   * whole frame and nothing on this screen has a height that has to be asked
   * for.
   */
  const scrollport = useScrollportMetrics()
  // ⌘F (brief §22, desktop keyboard cycle): the caret is inside a field on
  // the right, so the shortcut has to reach across to this box explicitly
  // rather than relying on focus already being here.
  const desktopSearchInputRef = useRef<HTMLInputElement>(null)
  // True while a touch paint gesture is active (long-press entry or touchmove).
  const isTouchPaintingRef = useRef(false)
  // True for the brief window between touchstart (potential paint) and the 10px
  // commitment threshold being crossed.
  const potentialTouchPaintRef = useRef(false)
  const touchPaintModeRef = useRef<'select' | 'deselect'>('select')
  // Last card id touched during paint — avoids redundant state updates.
  const touchPaintLastCardIdRef = useRef<string | null>(null)
  const touchStartYRef = useRef(0)
  // Gives touch handlers (stable closures) access to current-render cards and
  // selectedIds without stale-closure issues. Updated in the render body.
  const touchSelectionContextRef = useRef({
    cards: [] as import('../types').Card[],
    selection: EMPTY_SELECTION as DeckSelection,
    active: false,
  })

  const [searchParams] = useSearchParams()
  const targetCardId = searchParams.get('card')
  const nonce = searchParams.get('t')
  /**
   * The deep link's transient mark. Carries a sequence number beside the id so
   * a second arrival at the SAME card (the palette's nonce, below) is a new
   * value and restarts the mark, rather than writing the id it already holds
   * and leaving the first arrival's timer to expire under the second.
   */
  const [highlight, setHighlight] = useState<{ cardId: string; seq: number } | null>(null)
  const highlightSeqRef = useRef(0)
  const lastScrolledCardRef = useRef<string | null>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  /**
   * The deck's row elements, keyed by card id, written by the render that
   * creates them (`DeckDesktopPanels`' `rowRef`, which already registers the
   * same element for `⎋`'s focus return).
   *
   * This is how the deep-link motion gets an element to move to, and the
   * reason it does not ask the document for one. ADR-067 Decision 6:
   * "Scroll-to-card is computed from an index in the data, never by looking up
   * a node in the DOM" — and its do-not-reintroduce list names "scroll-to-card
   * by node lookup" outright. The index below decides WHICH card; the render
   * hands over the element, exactly as it hands the focus placement its row
   * and card (`onFieldFocus`) rather than being queried for them.
   *
   * It announces as well as stores — see `createDeckRowRegistry`. The arrival
   * effect lives here and the rows are created one component down, so a plain
   * map left the parent with no way to learn that the row it was waiting for
   * had appeared.
   */
  const rowRegistryRef = useRef<DeckRowRegistry | null>(null)
  rowRegistryRef.current ??= createDeckRowRegistry()
  const rowRegistry = rowRegistryRef.current
  /**
   * Bumped when a row the arrival was waiting for registers. The arrival's only
   * dependency that the registry itself can move — everything else it depends
   * on (the window, the target's index) can be settled while the row it needs
   * does not exist yet.
   */
  const [rowRegistryTick, setRowRegistryTick] = useState(0)
  /** The deep link's motion, so a second arrival supersedes the first rather
   * than running beside it. One writer to the scroller, always. */
  const deepLinkMotionRef = useRef<ScrollMotion | null>(null)

  /**
   * Which page the card feed starts at (brief §21.3 item 1), and what decided
   * it.
   *
   * `null` — and so page 1 — for every ordinary visit. Moved only by a
   * deliberate navigation: a quick jump by number, or a `?card=<uuid>` deep
   * link, both of which resolve to a page number and fetch THAT page. The chain
   * this replaces pulled pages 1..N sequentially: 35 requests and roughly 19
   * seconds against a blank screen on the largest deck in the system.
   *
   * `forArrival` names the arrival this anchor was chosen for — a card id and
   * the palette's nonce — so the screen can tell an anchor that answers the
   * link currently in the URL from one left over from a previous link or a
   * previous DECK. That distinction is the whole of the fetch gate below.
   * `targetIndex` is the deck-wide ordinal `locate` answered with, kept here
   * rather than re-read from the query so the window arithmetic survives that
   * query being disabled once its answer has been used.
   *
   * This state is per-deck and is cleared by name when the deck id changes —
   * see the reset block immediately below.
   */
  const [anchor, setAnchor] = useState<{
    page: number
    forArrival: string | null
    targetIndex: number | null
  } | null>(null)
  /**
   * ── Per-deck state, cleared when the deck changes ────────────────────────
   *
   * `/decks/:id` renders one element, so React reuses this component when only
   * the id moves — a deep link followed from one deck's page into another does
   * not remount it. Everything below describes ONE deck, and every one of them
   * used to survive into the next: the anchor governed which page of the new
   * deck was fetched, and an in-deck search left running on the old deck kept
   * the left panel reading the SEARCH query's results, so the target's page
   * arrived and was never what the list rendered.
   *
   * Reset during render, not from an effect, and above every query hook below.
   * An effect runs after the commit that already fetched — this runs before
   * anything for the new deck is requested, which is the property that makes
   * the wasted page request go away rather than merely be corrected afterwards.
   * (React re-renders immediately with the new state and discards this render's
   * output; the queries' own fetches are started from effects, which the
   * discarded render never reaches.)
   *
   * Remounting the page on the id instead — a `key` on the route — would reset
   * all of this for free and is deliberately not done: it throws away the deck
   * data cache with it and makes every deck-to-deck navigation a cold load.
   * The cost of resetting by name is that a new piece of per-deck state has to
   * be added here too. That is the intended cost.
   */
  const [stateDeckId, setStateDeckId] = useState(id)
  if (stateDeckId !== id) {
    setStateDeckId(id)
    setAnchor(null)              // which page of the deck the feed opens at
    setCardSearch('')            // the in-deck search box, and the query it runs
    setDesktopChip('all')        // the filter chip narrowing the list
    setSelection(EMPTY_SELECTION) // what a bulk action would act on
    setHighlight(null)           // the previous deep link's transient mark
    lastScrolledCardRef.current = null // the per-arrival scroll guard
  }

  const anchorPage = anchor?.page ?? 1
  /** The link currently in the URL, as one comparable value. */
  const arrivalKey = targetCardId ? `${targetCardId}|${nonce ?? ''}` : null
  /**
   * A deep link whose page is not yet known.
   *
   * While this holds, the feed fetches NOTHING. Page 1 is not a reasonable
   * guess to make in the meantime: on a deep link it is a request for a page
   * nobody asked for, it renders a window the reader did not ask to see, and
   * on the way in from another deck it used to be issued through THAT deck's
   * anchor. One deep link is one page request, for the page the card is on.
   */
  const awaitingDeepLinkPage = !!targetCardId && anchor?.forArrival !== arrivalKey

  const { data: deck, isLoading: deckLoading } = useDeck(id!)

  // «Карточки | Тексты», remembered per deck per device. Read above the early
  // returns like every hook; whether the switch is offered at all (an owner's own
  // deck) is decided below, once the deck is known.
  const [segment, setSegment] = useDeckSegment(id)
  const addTextToDeck = useAddTextToDeck({ id: id ?? '', title: deck?.title ?? '' })

  // The deep-link target's page, in one request (brief §21.3 item 1). The
  // server answers where the card sits; the effect below turns that into a page
  // and the feed opens there. Asked only while the answer is still needed —
  // once an anchor exists for this arrival the question is settled, and asking
  // again would only re-answer it.
  const {
    data: located,
    isError: locateFailed,
  } = useLocateCard(id, awaitingDeepLinkPage ? targetCardId : null)
  useEffect(() => {
    if (!awaitingDeepLinkPage) return
    if (located?.card_id === targetCardId) {
      setAnchor({
        page: Math.floor(located.index / PAGE_SIZE) + 1,
        forArrival: arrivalKey,
        targetIndex: located.index,
      })
    } else if (locateFailed) {
      // The deck does not hold the card. Stop waiting and open the deck at its
      // start — an unresolvable link must not leave the feed permanently
      // unfetched. `targetIndex: null` is what the arrival reads as "not here".
      setAnchor({ page: 1, forArrival: arrivalKey, targetIndex: null })
    }
  }, [awaitingDeepLinkPage, located, locateFailed, targetCardId, arrivalKey])

  const {
    data: cardsPages,
    isLoading: cardsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCardsInfinite(id!, PAGE_SIZE, anchorPage, !awaitingDeepLinkPage)
  const updateDeck = useUpdateDeck()
  const deleteDeck = useDeleteDeck()
  const toggleLearning = useToggleDeckLearning()
  const deleteCard = useDeleteCard()
  const moveCard = useMoveCard()
  const restoreCardOrder = useRestoreCardOrder()
  const deleteCards = useDeleteCards()
  const restoreCards = useRestoreCards()
  const moveCardsUndo = useMoveCards()
  const undoCardCopy = useUndoCardCopy()
  /**
   * Deck-wide counts — the only honest source for a number printed beside a
   * bulk action (brief §23 item 9). Fetched with the deck rather than on entry
   * into select mode, so `Select all (93)` is right on the first open, before
   * anything has been scrolled, and does not vary with what the cache held.
   */
  const cardCounts = useCardCounts(id!)
  /**
   * The desktop halves of the two Fill flows.
   *
   * The same hooks `DeckMobileFeed` calls — the deck-wide preflight/start/poll
   * sequence and the selection's own cost disclosure both live in
   * `useDeckFillFlows`, so `Fill empty cards` reaching the desktop menu costs a
   * hook call rather than a second implementation of the disclosure (which is
   * the thing that would then need fixing twice). Idle on mobile: neither flow
   * does anything until its sheet is opened, and only the desktop chrome below
   * opens these.
   */
  const desktopFillAll = useFillAllFlow(id!, desktopFillAllOpen, () => setDesktopFillAllOpen(false))
  const desktopFillSelection = useFillSelectionFlow(id!, selection, setSelection)
  const copyDeck = useCopyDeck()
  const currentUser = useAuthStore(selectUser)
  // Whether a selection is active — computed here, ahead of the back-gesture
  // sentinel below, so that effect can depend on this primitive boolean
  // rather than on the `selection` object itself. Selection mutates on every
  // card toggled in or out; the sentinel must push/pop once per entry into or
  // exit from select mode, not once per card.
  const isAnySelected = isSelectionActive(selection, cardCounts.data)

  /**
   * The anchored window as the screen understands it: the server's pages.
   *
   * Memoised up here, above the early returns, rather than computed below them
   * where it used to be. The deep-link effect needs the same array the panels
   * render — resolving a card's index against one list and rendering another
   * would be two answers to the question ADR-067 Decision 6 requires exactly
   * one of.
   */
  const cards = useMemo(
    () => cardsPages?.pages.flatMap((p) => p.items) ?? [],
    [cardsPages],
  )
  /**
   * Card id → its index in `cards`. One O(n) pass replaces n passes of O(n)
   * (brief §21.3 item 4), and it is the deep link's answer to "where is this
   * card in the data" whenever the card is already loaded.
   */
  const cardIndexById = useMemo(() => buildCardIndexMap(cards), [cards])

  /**
   * In-deck search on DESKTOP — the server's answer, never a filter over the
   * pages this page happens to hold.
   *
   * What stood here filtered `allCards`, so a deck larger than the fetched
   * window answered "No cards match" about cards it had simply not loaded: the
   * last live member of the ADR-067 Decision 6 family (a deck-wide operation
   * expressed through the client's loaded set), and the same endpoint the
   * mobile feed has used since 2026-08-04 fixes it. There is deliberately no
   * client-side fallback: a partial answer is indistinguishable from a complete
   * one, so a failed request is reported as a failure (see `desktopSearchStatus`
   * below) rather than quietly downgraded to an empty result.
   */
  const desktopQuery = cardSearch.trim()
  const desktopSearch = useCardSearchInfinite(id!, isDesktop ? desktopQuery : '', PAGE_SIZE)
  const desktopSearching = isDesktop && desktopQuery.length > 0
  const searchResults = useMemo(
    () => desktopSearch.data?.pages.flatMap((p) => p.items) ?? [],
    [desktopSearch.data],
  )
  /** Deck-wide match count, or null while it is unknown. */
  const searchTotal = desktopSearch.data?.pages[0]?.total ?? null
  const desktopSearchStatus: DeckSearchStatus = !desktopSearching
    ? 'idle'
    : desktopSearch.isError
      ? 'error'
      // `isFetching` rather than `isLoading`: `placeholderData` keeps the
      // previous page visible while a retyped query is in flight, and calling
      // that "results" would print the old count under the new word.
      : (desktopSearch.isFetching && searchTotal === null) || desktopSearch.isPending
        ? 'loading'
        : searchTotal === 0
          ? 'empty'
          : 'results'

  // Reset the scroll guard whenever the target card OR nonce changes.
  // Nonce changes on every palette click, so re-clicking the same card still re-fires.
  useEffect(() => {
    lastScrolledCardRef.current = null
  }, [targetCardId, nonce])

  /**
   * Where the target sits in the DATA, as a row of the currently anchored
   * window — the index ADR-067 Decision 6 requires the scroll to be computed
   * from, and the same value that decided which page to open.
   *
   * Two sources, one meaning. Loaded: the index map, which is already an index
   * into the very array the panels render. Not loaded: the server's deck-wide
   * ordinal carried on the anchor, less the anchor's offset — the inverse of
   * the arithmetic that turned that ordinal into `anchorPage`, and the same
   * conversion the phone's quick jump makes (`windowIndexOf`). It is read off
   * the anchor rather than off the `locate` query because that query is
   * deliberately switched off once its answer has been used.
   *
   * A value outside the window means the anchored page has not arrived yet.
   * That is a WAIT, not a failure: the effect below leaves its guard unconsumed
   * and runs again when the window settles.
   */
  const targetWindowIndex = useMemo(() => {
    if (!targetCardId) return null
    const loaded = cardIndexById.get(targetCardId)
    if (loaded !== undefined) return loaded
    if (anchor?.forArrival !== arrivalKey || anchor.targetIndex === null) return null
    return anchor.targetIndex - (anchorPage - 1) * PAGE_SIZE
  }, [targetCardId, cardIndexById, anchor, arrivalKey, anchorPage])

  /**
   * Whether the window is still moving towards the target — the difference
   * between "not here yet" and "not here at all".
   *
   * The desktop arrival never has to tell the two apart: it simply waits, and a
   * target that never arrives leaves the screen unmarked. The phone's arrival
   * reports the failure instead of leaving the reader looking for a mark that
   * is not coming, so it needs the moment at which waiting stops being honest.
   *
   * `awaitingDeepLinkPage` covers everything up to the anchor being chosen —
   * the `locate` round trip, and the commit gap between its answer landing and
   * the anchor moving onto it (effects run child-first, so the feed sees the
   * server's answer one commit before the anchor has been set from it). The
   * cards terms cover the page request that follows. Without the first term,
   * every deep link to a card outside the first page would report itself
   * missing in the instant before its page was even requested.
   */
  const deepLinkPending = useMemo(() => {
    if (!targetCardId) return false
    return awaitingDeepLinkPage || cardsLoading || isFetchingNextPage
  }, [targetCardId, awaitingDeepLinkPage, cardsLoading, isFetchingNextPage])

  /**
   * The reader moving the window themselves — the phone feed's quick jump by
   * number, and the "back to the start" beside the anchor notice on both
   * layouts.
   *
   * It keeps `forArrival` so the move does not read as an unanswered deep link
   * and re-hold the fetch, and it keeps whatever ordinal `locate` already
   * found: the card has not moved just because the reader looked elsewhere, so
   * an arrival still in flight recomputes its row against the new window rather
   * than losing track of the card entirely.
   */
  const moveAnchor = useCallback((page: number) => {
    setAnchor((prev) => ({
      page,
      forArrival: arrivalKey,
      targetIndex: prev?.forArrival === arrivalKey ? prev.targetIndex : null,
    }))
  }, [arrivalKey])

  /**
   * A card's number in the DECK, for display.
   *
   * Separate from `indexOf`, which is a position in the loaded window and is
   * what the row logic wants (shift-range selection, insert dividers, `⇥`
   * between cards). Printing that same value was what made an anchored desktop
   * window unreadable: opened at page 5, its rows were numbered 1–30, so the
   * deck looked as though it were showing its beginning. The phone feed has
   * always numbered its rows this way (`numberOf`); this is that, not a second
   * version of it. A search match carries the server's own deck-wide index and
   * needs no offset.
   */
  const numberOf = useCallback((card: Card | CardSearchItem) => (desktopSearching
    ? ((card as CardSearchItem).index ?? 0) + 1
    : (anchorPage - 1) * PAGE_SIZE + (cardIndexById.get(card.id) ?? 0) + 1
  ), [desktopSearching, anchorPage, cardIndexById])

  /**
   * Start the deep link's mark. Both layouts arrive by their own route — the
   * effect below on the desktop, `DeckMobileFeed`'s own arrival on the phone —
   * and both end here, so the mark means one thing and expires one way.
   */
  const markDeepLinkTarget = useCallback((cardId: string) => {
    highlightSeqRef.current += 1
    setHighlight({ cardId, seq: highlightSeqRef.current })
  }, [])

  // Arrive at the deep link on the DESKTOP layout: move to the target row, and
  // mark it briefly. The phone's arrival is the same shape, made where its rows
  // are — see `DeckMobileFeed`'s own arrival effect. Two effects rather than one
  // because the two feeds narrow their rows differently (the phone's chip and
  // search live inside it) and because only one of them is ever mounted, so
  // there is never a moment when both could write to the scroller.
  //
  // The index says which card; `cards[index]` says which id; the registry the
  // render populated says which element. Nothing here asks the document for a
  // node, and nothing here derives a scroll offset from the index — the list
  // is deliberately not virtualised (Decision 6), so there is no row-height
  // model to derive one from and building one is the thing that decision
  // declines to do.
  //
  // The motion is `createScrollMotion` — the same single-writer, retargetable
  // ease the focus placement on this surface uses, which yields to the reader's
  // own wheel or touch rather than holding a lock (the assistance principle of
  // the 2026-07-30 amendment). A previous deep-link motion is superseded, never
  // raced.
  useEffect(() => {
    if (!isDesktop) return
    if (!targetCardId) return
    if (lastScrolledCardRef.current === targetCardId) return
    if (targetWindowIndex === null) return
    // The window has not settled on the located page yet.
    if (cards[targetWindowIndex]?.id !== targetCardId) return
    const row = rowRegistry.get(targetCardId)
    // The target is in the data, at a known index, and its row does not exist
    // yet: the list is not rendered at all (the page is still its own skeleton
    // while the deck request is out), or a desktop search is showing a
    // different list.
    //
    // This used to `return` and count on some later render re-running the
    // effect. Nothing guaranteed one. Every other dependency here — the window,
    // the target's index, the card id — can be settled BEFORE the rows exist,
    // and the commit that finally creates them changes none of them, so the
    // effect that gave up was frequently the last one to run: the card sat on
    // screen, correctly numbered, unscrolled and unmarked. Now the registry
    // says when the row lands, and the cancel goes back with the effect so a
    // superseded arrival does not fire.
    if (!row?.isConnected) {
      const cancel = rowRegistry.notifyWhenRegistered(targetCardId, () => {
        setRowRegistryTick((t) => t + 1)
      })
      return () => { cancel() }
    }
    // The element that scrolls the row, as declared by the layout that rendered
    // both (`DeckRowRegistry.setScrollport`): on this layout, the card list's
    // own scroll region, never the page. The desktop page has no scroll range
    // at all — the frame spans the scrollport and the deck's chrome lives in
    // the screen-header bar (ADR-067, tenth and thirteenth amendments) — so a
    // motion aimed at the page would go nowhere and the mark would land on a
    // row the reader cannot see. Which element scrolls the rows is a property
    // of the layout, so the layout says it; this does not walk up from the row.
    const scroller = rowRegistry.scrollport()
    if (!scroller) return

    lastScrolledCardRef.current = targetCardId

    // The one write this arrival makes (ADR-067 Decision D). There used to be
    // a page pre-scroll before it, bringing a sticky shell to rest under the
    // chrome above it; it went with the sticky shell on 2026-09-12, and with
    // the band gone too there is nothing in flow above the list for anything
    // to scroll past. The list's scroll region is fully on screen from the
    // first paint, so the rects read below are the reader's own.
    const rowRect = row.getBoundingClientRect()
    const viewRect = scroller.getBoundingClientRect()
    // Centred in the scrollport, which is what the `block: 'center'` this
    // replaces asked for. Read from live rects, like every other scroll target
    // on this screen; `scrollTop + delta` is absolute, so it stays correct if
    // the motion is re-aimed from wherever it currently stands.
    const delta = (rowRect.top + rowRect.height / 2) - (viewRect.top + viewRect.height / 2)
    const reducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches

    deepLinkMotionRef.current?.abort()
    const motion = createScrollMotion(scroller)
    deepLinkMotionRef.current = motion
    motion.to(Math.max(0, scroller.scrollTop + delta), reducedMotion ? 0 : PLACEMENT_DURATION)

    const yieldToUser = () => { if (motion.inFlight()) motion.abort() }
    scroller.addEventListener('touchmove', yieldToUser, { passive: true })
    scroller.addEventListener('wheel', yieldToUser, { passive: true })

    markDeepLinkTarget(targetCardId)

    return () => {
      scroller.removeEventListener('touchmove', yieldToUser)
      scroller.removeEventListener('wheel', yieldToUser)
    }
    // `rowRegistryTick` is how the registry's own answer gets in here: it moves
    // only when a row this effect asked about registers, so it re-runs the
    // arrival exactly once, on the commit where its row first exists, and
    // never merely because something else on the page changed.
  }, [isDesktop, targetCardId, nonce, targetWindowIndex, cards, rowRegistry, rowRegistryTick, markDeepLinkTarget])

  // The mark's own lifetime, kept out of the effect above so that a card
  // mutation landing mid-mark (which changes `cards`, and so re-runs it)
  // cannot cut the mark short by tearing down its timer.
  useEffect(() => {
    if (!highlight) return
    const timer = setTimeout(() => {
      setHighlight(null)
    }, 1800)
    return () => clearTimeout(timer)
  }, [highlight])

  // Leaving the screen mid-motion: the scroller outlives this page, so the
  // loop has to be stopped rather than left writing to it.
  useEffect(() => () => { deepLinkMotionRef.current?.abort() }, [])

  // IntersectionObserver: load next page when user scrolls to the bottom
  // sentinel. Whichever list is on screen is the one that pages — a search
  // matching more than a page's worth streams the rest the same way the deck
  // itself does, rather than stopping at thirty with no way to reach the rest.
  const listHasNextPage = desktopSearching ? !!desktopSearch.hasNextPage : !!hasNextPage
  const listFetchingNext = desktopSearching ? desktopSearch.isFetchingNextPage : isFetchingNextPage
  const listFetchNext = desktopSearching ? desktopSearch.fetchNextPage : fetchNextPage
  useEffect(() => {
    const el = loadMoreRef.current
    if (!el || !listHasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !listFetchingNext) listFetchNext()
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [listHasNextPage, listFetchingNext, listFetchNext])
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    setIsDesktop(mq.matches)
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  // The two-pane threshold within the desktop branch (design brief §4h,
  // brief §23 item 8's second half): below 1040px the pane pair no longer
  // fits — the left pane would fall under the 560px its label column plus
  // one line of content needs — and `DeckDesktopPanels` switches the right
  // panel to a sheet over the full-width list. Independent of `isDesktop`
  // above (768px): a window can be "desktop" by that measure and still be
  // too narrow for two panes.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1039px)')
    setNarrowPanels(mq.matches)
    const handler = (e: MediaQueryListEvent) => setNarrowPanels(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  // A chip moving under an active whole-deck selection rebases it: the old
  // exclusions were about a different set, so keeping them would make the count
  // a guess. Same rule `DeckMobileFeed` applies to its own chip — and guarded on
  // `isDesktop` for exactly that reason: the feed owns the chip on the phone,
  // and this effect firing there would overwrite the feed's filter with the
  // desktop chip's permanent `'all'`.
  useEffect(() => {
    if (!isDesktop) return
    setSelection((prev) => selectionFilterChanged(prev, desktopChip))
  }, [isDesktop, desktopChip])
  // Close "···" menu on Escape key. Stays its own listener: it is rung 1's
  // own closing action, and the ladder below deliberately yields to overlays
  // rather than closing them (see `utils/deckEscapeLadder`).
  useEffect(() => {
    if (!showMoreMenu) return
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowMoreMenu(false) }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [showMoreMenu])

  /**
   * The deck desktop screen's single Escape ladder — the one listener that
   * decides which of the screen's five dismissible levels a press resolves to.
   * The order and the reasoning behind it live in `utils/deckEscapeLadder`;
   * this effect only supplies the current state and performs the action.
   *
   * Desktop only. The phone reaches every one of these levels through the
   * Android back button instead, via the `useHistorySentinel` stack just
   * below, which already sequences them LIFO — a second mechanism there would
   * be two things racing for one gesture.
   *
   * `defaultPrevented` is checked first and is not one of the rungs: it means
   * a more specific owner INSIDE the screen already handled this press — the
   * active card's own key handler, the field-type popover, a field drag being
   * cancelled, the paste offer. Those own a level finer-grained than anything
   * this ladder models, and they announce it the same way throughout the
   * codebase.
   */
  const panelsRef = useRef<DeckDesktopPanelsHandle>(null)
  const overlayOpen = (
    showMoreMenu || confirmDeleteDeck || confirmDeleteCardId !== null || confirmBulkDelete
    || showTransferModal || showImport || referenceCardId !== null || referenceTerm !== ''
    || editingTitle !== null || editingDescription !== null
    || reorderScreenOpen || mobileFillAllOpen
  )
  useEffect(() => {
    if (!isDesktop) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      const panels = panelsRef.current
      const rung = resolveDeckEscapeRung({
        overlayOpen,
        editSessionOpen: panels?.isEditSessionOpen() ?? false,
        searchNonEmpty: cardSearch !== '',
        deckModeOn: deckEditMode,
        cardSelected: panels?.hasSelection() ?? false,
      })
      switch (rung) {
        // Yielded to, never closed here — the overlay's own handler owns that.
        case 'overlay': return
        case 'edit-session': e.preventDefault(); panels?.endEditSession(); return
        case 'search': e.preventDefault(); setCardSearch(''); return
        case 'deck-mode': e.preventDefault(); setDeckEditMode(false); return
        case 'selection': e.preventDefault(); panels?.clearSelection(); return
        case 'none': return
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isDesktop, overlayOpen, cardSearch, deckEditMode])
  // Android back leaves each of these modes instead of exiting the deck —
  // §7.1's history-sentinel mechanism, one `useHistorySentinel` call per
  // mode, all sharing the same hook rather than each reinventing it. Modes
  // nest (editing mode, search opened over it, a selection made inside it,
  // …): real browser history is LIFO, and `useHistorySentinel` checks its
  // own key survived the pop before acting, so a single back press resolves
  // exactly the innermost mode currently open — see the hook's own comment
  // for why a nested sentinel cannot just remove itself after one fire.
  //
  // `···` menu — closes without navigating away.
  useHistorySentinel(showMoreMenu && !isDesktop, 'deckMenuSentinel', () => setShowMoreMenu(false))
  // Search (brief §15, 2026-08-04 device-pass fix) — can sit over editing
  // mode; back leaves search first, editing stays open.
  useHistorySentinel(mobileSearchOpen && !isDesktop, 'deckSearchSentinel', () => setMobileSearchOpen(false))
  // Multi-select — can be entered from inside editing mode (long-press a
  // row) as well as from plain view; back clears the selection, whatever
  // mode it was entered from stays. Clearing `selection` is the same call
  // the bar's Cancel/X already makes (`exitSelectMode`); it does not reach
  // into a bulk action in flight (Fill/Move/Copy/Delete run independently of
  // `selection`) or an open Undo window, so leaving this way neither
  // performs nor cancels an action nor touches Undo.
  useHistorySentinel(isAnySelected && !isDesktop, 'deckSelectSentinel', () => setSelection(EMPTY_SELECTION))
  // Editing mode itself — the gap this pass closes. `exitEditingMode` is the
  // exact function `Done` calls, so back is indistinguishable from Done: no
  // pending edit discarded that Done would have saved, no undo window
  // touched, no scroll write. A card active inside editing mode carries its
  // own nested sentinel in DeckMobileFeed, so the first back press
  // deactivates the card and only a further press reaches this one.
  useHistorySentinel(proofreadMode && !isDesktop, 'deckEditSentinel', exitEditingMode)
  // Audit (same pass as the editing-mode fix above): three more management-
  // layer panels are reached from the "···" menu the same way search always
  // was — `setShowMoreMenu(false)` fires in the same click as opening them,
  // which pops the menu's own sentinel before the new panel ever mounted one
  // of its own, so back fell straight through to the deck. Each has its own
  // exit already (`onCloseFilter`, `onCloseFillAll`, `DeckReorderScreen`'s
  // `Done`) and is wired the identical way.
  useHistorySentinel(mobileFilterOpen && !isDesktop, 'deckFilterSentinel', () => setMobileFilterOpen(false))
  useHistorySentinel(mobileFillAllOpen && !isDesktop, 'deckFillAllSentinel', () => setMobileFillAllOpen(false))
  useHistorySentinel(reorderScreenOpen && !isDesktop, 'deckReorderSentinel', () => setReorderScreenOpen(false))
  // The Move/Copy destination sheet — missed by the audit above. It nests one
  // level inside multi-select (opened from the selection bar's Move/Copy),
  // so back closes only the sheet and the selection survives underneath —
  // the reader can pick a different destination or act again; a further back
  // then leaves multi-select. `onClose` here is the exact function the
  // sheet's own Cancel button calls (below), so back and Cancel abandon the
  // same thing: the destination choice, not the selection. Neither performs
  // or partially performs the transfer, and neither touches an Undo window
  // from an earlier operation.
  //
  // A completed transfer closes the sheet AND clears the selection in one
  // commit (`onSuccess` below) — two sentinels unregistering together, which
  // is what surfaced the `expectedSelfPops` overcount the manager now
  // accounts for (a single `go(-n)` is one `popstate`, not `n`).
  useHistorySentinel(showTransferModal && !isDesktop, 'deckTransferSentinel', () => setShowTransferModal(false))
  // Stop paint gesture on pointer release anywhere in the document.
  // Also restores text-selection (disabled during paint to stop text being swept).
  useEffect(() => {
    const stop = () => {
      isPaintingRef.current = false
      document.body.style.userSelect = ''
    }
    document.addEventListener('pointerup', stop)
    document.addEventListener('pointercancel', stop)
    return () => {
      document.removeEventListener('pointerup', stop)
      document.removeEventListener('pointercancel', stop)
    }
  }, [])

  // Touch paint-selection listeners — { passive: false } so we can call
  // preventDefault() to block scroll once the gesture commits to painting.
  // All mutable values are read through refs so the handlers never go stale.
  useEffect(() => {
    const el = cardListRef.current
    if (!el) return

    function cardAtPoint(x: number, y: number): { cardId: string } | null {
      const target = document.elementFromPoint(x, y)
      if (!target) return null
      // Skip touches that land on the grip handle (drag-to-reorder target).
      if ((target as Element).closest('[data-drag-handle]')) return null
      const wrapper = (target as Element).closest('[data-card-id]') as HTMLElement | null
      if (!wrapper) return null
      const cardId = wrapper.dataset.cardId
      return cardId ? { cardId } : null
    }

    function onTouchStart(e: TouchEvent) {
      const { selection, active } = touchSelectionContextRef.current
      // Only prime a paint gesture when select mode is already on.
      if (!active) return
      const touch = e.touches[0]
      const hit = cardAtPoint(touch.clientX, touch.clientY)
      if (!hit) return
      touchStartYRef.current = touch.clientY
      potentialTouchPaintRef.current = true
      touchPaintLastCardIdRef.current = hit.cardId
      touchPaintModeRef.current = isCardSelected(selection, hit.cardId) ? 'deselect' : 'select'
    }

    function onTouchMove(e: TouchEvent) {
      const touch = e.touches[0]

      // Case A: already committed to paint (long-press then slide, or threshold crossed).
      if (isTouchPaintingRef.current) {
        e.preventDefault()
        const hit = cardAtPoint(touch.clientX, touch.clientY)
        if (!hit || hit.cardId === touchPaintLastCardIdRef.current) return
        touchPaintLastCardIdRef.current = hit.cardId
        setSelection((prev) =>
          setCardSelected(prev, hit.cardId, touchPaintModeRef.current === 'select'))
        return
      }

      // Case B: potential paint — wait for 10 px vertical commitment.
      if (potentialTouchPaintRef.current) {
        const deltaY = Math.abs(touch.clientY - touchStartYRef.current)
        if (deltaY > 10) {
          potentialTouchPaintRef.current = false
          isTouchPaintingRef.current = true
          e.preventDefault()
          const hit = cardAtPoint(touch.clientX, touch.clientY)
          if (!hit || hit.cardId === touchPaintLastCardIdRef.current) return
          touchPaintLastCardIdRef.current = hit.cardId
          setSelection((prev) =>
            setCardSelected(prev, hit.cardId, touchPaintModeRef.current === 'select'))
        }
      }
    }

    function onTouchEnd() {
      isTouchPaintingRef.current = false
      potentialTouchPaintRef.current = false
      touchPaintLastCardIdRef.current = null
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    el.addEventListener('touchcancel', onTouchEnd, { passive: true })
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
    }
  }, []) // Stable: all mutable state accessed via refs; setSelection is stable

  // ── END of hooks ──────────────────────────────────────────────────────────

  // Early returns AFTER all hooks
  if (deckLoading) return <DeckDetailSkeleton />
  if (!deck) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
      Deck not found.
    </div>
  )

  // What the left panel lists: the server's matches while a search is running,
  // the loaded window otherwise. Never a blend of the two — see the comment on
  // `desktopSearchStatus`.
  //
  // The chip narrows the window, exactly as it does in the feed: a persistent
  // narrowing whose deck-wide COUNTS come from the server, so it never claims to
  // have seen more of the deck than it has. It does not apply to search results
  // — a search already answers over the whole deck, and intersecting the two
  // would narrow a server answer by a client rule.
  const desktopFilterActive = desktopChip !== 'all'
  // Whichever surface is showing, is a filter currently narrowing the list? The
  // one question the ⋯ menu's Reorder refusal asks.
  const filterNarrowingList = isDesktop ? desktopFilterActive : mobileFilterActive
  const displayedCards = desktopSearching
    ? searchResults
    : desktopFilterActive ? filterCards(cards, desktopChip) : cards
  // Badge count for the mobile "Fill empty cards" menu item — same
  // definition as DeckMobileFeed's "Not filled" chip (Prompt 8). Computed
  // directly (not memoized) since this runs after the early returns above,
  // where hooks can no longer be called.
  // Deck-wide, from the server — the badge sits on a menu item that starts a
  // Fill over the whole deck, so counting the loaded pages here understated the
  // work exactly as §23 item 9 describes. 0 until the counts arrive, which
  // hides the sublabel rather than showing a wrong one.
  const notFilledCount = cardCounts.data?.not_filled ?? 0

  // Editable only by the owning user. System decks are never editable.
  const isOwner = isDeckOwner(deck, currentUser?.id)
  // A deck that cannot be edited at all — a system deck, or one the reader
  // does not own — has no editing entry and no deck mode: reading is its only
  // state. The gate is applied here, once, so neither the bar's action nor the
  // panels can disagree about it.
  const headerCanEditCards = !deck.is_system && isOwner

  // ── «Карточки | Тексты» (handoff §8, §19.6) ──
  // Offered only on the learner's own deck: a text is linked to an own deck, so
  // a system deck, or a deck someone else owns, has none to show and keeps this
  // screen exactly as it was.
  const showTextsSwitch = isOwner
  // A link to one card (?card=) is a card question: it opens the cards whatever
  // segment was last left on, which the stored choice is left untouched by.
  const showTexts = showTextsSwitch && segment === 'texts' && targetCardId === null
  const shownSegment: DeckSegment = showTexts ? 'texts' : 'cards'
  const textCount = deck.text_count ?? 0
  function selectSegment(next: DeckSegment) {
    if (next === shownSegment) return
    if (next === 'texts') {
      // The card modes belong to the card list, which is about to leave the
      // screen: a selection or a deck-wide edit mode left on would still be
      // acting on cards the learner can no longer see.
      setSelection(EMPTY_SELECTION)
      setDeckEditMode(false)
    }
    setSegment(next)
  }

  const deckCounts = cardCounts.data
  const countsUnavailable = cardCounts.isError
  // `isAnySelected` (unknown count still counts as "in select mode": the
  // reader tapped `Select all` and the toolbar must not blink out while the
  // count is in flight) is computed earlier, above the sentinel effects.
  const selectedCount = selectionCount(selection, deckCounts)
  const wholeDeckSelected = selection.scope === 'deck'
  // What `Select all` would select on desktop — the DECK's count for the active
  // chip, from the server. `null` while it is unknown, which the label says
  // rather than standing in a loaded-set number.
  const desktopSelectableTotal = selectionCount(selectWholeDeck(desktopChip), deckCounts)

  // The desktop frame — see `scrollport` above. `null` until the scrollport
  // has been measured, and the screen renders at its natural height for that
  // one frame rather than at a guess.
  const deckFrame = isDesktop && scrollport
    ? deckScreenFrame(scrollport)
    : null

  /**
   * The list column's shown-count (§13): how many cards the rows below it are
   * drawn from. Deck-wide in every branch, never the loaded window's length —
   * the count sits beside a search box and a filter, which are exactly the two
   * things that make "how many are there" a question, and answering it with
   * however many pages happen to be in memory is the defect brief §23 item 9
   * describes.
   */
  const shownCount = desktopSearching
    ? (searchTotal !== null ? dm.resultCount(searchTotal, deck.card_count) : null)
    : desktopFilterActive
      ? (desktopSelectableTotal !== null ? dm.resultCount(desktopSelectableTotal, deck.card_count) : null)
      : dd.cardCount(deck.card_count)

  // Keep touch handlers in sync with current render values (ref, not state,
  // so closures inside the effect never go stale between renders).
  touchSelectionContextRef.current = { cards, selection, active: isAnySelected }

  function showToast(msg: string) {
    setToastMsg(msg)
    setTimeout(() => setToastMsg(null), 3500)
  }

  function dismissUndo() {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current)
    undoTimerRef.current = null
    setUndoPending(null)
    setUndoError(false)
    setUndoWorking(false)
  }

  /**
   * How long the Undo offer stays up.
   *
   * A move or a copy is visible in its target the moment it happens, and can be
   * walked back by hand afterwards; ten seconds is a convenience. Six seconds
   * for a delete (ADR-077) is this same on-screen offer's own window, not the
   * recovery window: the server-held snapshot behind it (`CardDeletionBatch`,
   * `SNAPSHOT_TTL` = 1 hour) is untouched by this constant and is what makes
   * the deleted cards recoverable long after this toast itself is gone.
   */
  function scheduleUndoDismiss(mode: UndoPending['mode']) {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current)
    undoTimerRef.current = setTimeout(dismissUndo, mode === 'delete' ? 6_000 : 10_000)
  }

  async function handleUndo() {
    if (!undoPending || undoWorking) return
    setUndoWorking(true)
    setUndoError(false)
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current)
    try {
      if (undoPending.mode === 'move') {
        // Explicit ids, always: undo names the exact cards the move reported
        // back, which is a real list however the original selection was
        // expressed.
        await moveCardsUndo.mutateAsync({
          sourceDeckId: undoPending.destDeckId,
          targetDeckId: undoPending.sourceDeckId,
          selection: { mode: 'ids', card_ids: undoPending.cardIds },
        })
        // Best-effort: restore the source deck's order. The cards are already
        // back regardless of whether this step succeeds, so a failure here
        // does not roll back the move itself — but it must not be reported as
        // success either. `dismissUndo` still runs on failure (there is
        // nothing left to retry through the Undo button: the cards are placed,
        // only their order is off), but the toast says exactly that, instead
        // of the "may have shifted" hedge this replaced.
        if (undoPending.originalOrder.length > 0) {
          try {
            await restoreCardOrder.mutateAsync({
              deckId: undoPending.sourceDeckId,
              items: undoPending.originalOrder,
            })
          } catch {
            dismissUndo()
            showToast(dd.toastOrderNotRestored)
            return
          }
        }
        // The cards come back selected.
        //
        // An Undo puts the reader back where they were, and where they were was
        // holding a selection — most often because the destination was wrong,
        // which means the very next thing they want is to move the same cards
        // somewhere else. Clearing it made them rebuild the selection by hand,
        // and on a deck-scoped move that is not even possible card by card.
        //
        // As explicit ids, not as the scope the move was expressed in: these
        // are the cards the server just reported moving back, so the list is
        // real however the original selection was worded — and a `deck` scope
        // restored here would silently re-widen to whatever the deck holds now,
        // which is not what was undone.
        setSelection({ scope: 'ids', ids: new Set(undoPending.cardIds) })
      } else if (undoPending.mode === 'delete') {
        // Restoring, not re-creating. The server holds the snapshot; the cards
        // come back under their own ids, at their own positions, with their
        // learning state — and the response says how much of that actually
        // landed. Whatever fell short is reported here rather than smoothed
        // into "Undone", because the one thing a delete-undo can quietly get
        // wrong is returning the cards while resetting a reader's progress.
        const result = await restoreCards.mutateAsync({
          deckId: undoPending.deckId,
          restoreToken: undoPending.restoreToken,
        })
        dismissUndo()
        const back = result.cards_restored + result.cards_already_present
        if (result.complete) showToast(dd.toastRestored(back))
        else if (back < result.cards_expected) {
          showToast(dd.toastRestorePartial(back, result.cards_expected))
        } else if (!result.learning_state_complete) {
          showToast(dd.toastRestoredWithoutProgress(back))
        } else {
          showToast(dd.toastRestoredWithoutLinks(back))
        }
        return
      } else {
        // Through the operation's token, not through card ids — see the
        // `UndoPending` copy variant for why ids cannot name a copy.
        const result = await undoCardCopy.mutateAsync({
          deckId: undoPending.destDeckId,
          undoToken: undoPending.undoToken,
        })
        dismissUndo()
        // A copy the reader has since moved or deleted is no longer this
        // operation's to remove, and the server says so rather than reaching
        // for it. Saying "undone" over a partial would be the same overclaim
        // the delete-restore breakdown exists to avoid.
        showToast(result.complete
          ? dd.toastUndone
          : dd.toastCopyUndoPartial(result.cards_removed, result.cards_expected))
        return
      }
      dismissUndo()
      showToast(dd.toastUndone)
    } catch {
      setUndoWorking(false)
      setUndoError(true)
    }
  }

  function handleToggleSelect(cardId: string) {
    setSelection((prev) => toggleCardSelected(prev, cardId))
  }

  /**
   * `Select all` — the DECK, as a scope the server resolves.
   *
   * Was `new Set(cards.map(...))`, i.e. the pages this page had fetched, which
   * is the defect: on a 93-card deck it selected 30 and the counter beside it
   * said 30 as though that were the deck (brief §23 item 9).
   *
   * It carries the active chip, so `Not filled` + `Select all` is every
   * not-filled card in the deck rather than the not-filled cards among the pages
   * fetched so far — the same expression the feed's own `Select all` uses.
   */
  function handleSelectAll() { setSelection(selectWholeDeck(desktopChip)) }
  function handleDeselectAll() { setSelection(EMPTY_SELECTION) }
  function handleMoveSingleCard(cardId: string) {
    setSelection({ scope: 'ids', ids: new Set([cardId]) })
    setTransferMode('move')
    setShowTransferModal(true)
  }

  // Called by the checkbox onClick. Handles shift-range or plain toggle.
  function handleCheckboxClick(e: React.MouseEvent, cardId: string, cardIndex: number) {
    if (suppressCheckboxClickRef.current) {
      // pointerdown already handled the toggle; skip to avoid double-toggling.
      suppressCheckboxClickRef.current = false
      return
    }
    if (e.shiftKey && anchorCardId !== null) {
      const anchorIdx = cards.findIndex((c) => c.id === anchorCardId)
      if (anchorIdx !== -1) {
        const lo = Math.min(anchorIdx, cardIndex)
        const hi = Math.max(anchorIdx, cardIndex)
        const rangeIds = cards.slice(lo, hi + 1).map((c) => c.id)
        setSelection((prev) => addCardsSelected(prev, rangeIds))
      }
      // Anchor stays at the original card after a shift-click.
    } else {
      handleToggleSelect(cardId)
      setAnchorCardId(cardId)
    }
  }

  // Called by the checkbox onPointerDown. Starts the paint-selection gesture.
  // Guard: mouse only (pointerType === 'mouse') so touch long-press is unaffected.
  function handleCheckboxPointerDown(e: React.PointerEvent, cardId: string) {
    if (e.pointerType !== 'mouse' || e.button !== 0 || e.shiftKey) return
    e.preventDefault() // prevent text selection while sweeping
    document.body.style.userSelect = 'none'
    suppressCheckboxClickRef.current = true
    isPaintingRef.current = true
    paintModeRef.current = isCardSelected(selection, cardId) ? 'deselect' : 'select'
    setSelection((prev) => setCardSelected(prev, cardId, paintModeRef.current === 'select'))
    setAnchorCardId(cardId)
  }

  // Called by onPointerEnter on each checkbox zone while the paint gesture is live.
  function handleCheckboxPointerEnter(cardId: string) {
    if (!isPaintingRef.current) return
    setSelection((prev) => setCardSelected(prev, cardId, paintModeRef.current === 'select'))
  }

  // These handlers are defined as const arrows (not hoisted `function`s) so TS
  // carries the `if (!deck) return` narrowing above into their bodies — `deck`
  // is `Deck` here, never undefined, since the component only renders them once
  // the deck has loaded. Runtime behavior is unchanged.
  /**
   * Bulk delete.
   *
   * The selection goes to the server as a scope; the toast reports the server's
   * own `deleted`, not the number the client hoped for. When `deleted` falls
   * short of `requested` it says so rather than presenting a partial as a plain
   * success — the constraint that made this item urgent.
   */
  const handleBulkDelete = () => {
    deleteCards.mutate(
      { deckId: deck.id, selection: toSelectionRequest(selection) },
      {
        onSuccess: ({ deleted, requested, restore_token: restoreToken }) => {
          setConfirmBulkDelete(false)
          setSelection(EMPTY_SELECTION)
          dismissUndo()
          // The Undo offer, when the server kept something to restore. A
          // partial delete still gets one — what came back is then what was
          // actually removed, not what was asked for, which is why the toast
          // below quotes `deleted`.
          if (restoreToken && deleted > 0) {
            setUndoPending({
              mode: 'delete', count: deleted, requested, deckId: deck.id, restoreToken,
            })
            scheduleUndoDismiss('delete')
            return
          }
          showToast(deleted === requested
            ? dd.toastDeleted(deleted)
            : dd.toastDeletePartial(deleted, requested))
        },
      },
    )
  }

  const handleCopyShareLink = async () => {
    if (!deck.share_token) return
    const result = await shareLink({
      title: deck.title,
      url: `${window.location.origin}/shared/deck/${deck.share_token}`,
    })
    if (result === 'clipboard') showToast(dd.toastLinkCopied)
  }

  const handleExport = async () => {
    setExporting(true)
    try { await exportDeckCsv(deck.id, deck.title) }
    finally { setExporting(false) }
  }

  const handleTogglePublic = () => {
    updateDeck.mutate({ id: deck.id, is_public: !deck.is_public })
  }

  const handleSaveTitle = () => {
    const val = (editingTitle ?? '').trim()
    setEditingTitle(null)
    if (!val || val === deck.title) return
    updateDeck.mutate(
      { id: deck.id, title: val },
      { onError: (err) => showToast(extractErrorMessage(err, dd.toastEditDetailsError)) },
    )
  }

  const handleSaveDescription = () => {
    const val = (editingDescription ?? '').trim() || null
    setEditingDescription(null)
    if (val === deck.description) return
    updateDeck.mutate(
      { id: deck.id, description: val },
      { onError: (err) => showToast(extractErrorMessage(err, dd.toastEditDetailsError)) },
    )
  }

  // Desktop's Edit details dialog: one save for both fields, since the dialog
  // is one edit. The phone keeps its two in-place editors above.
  const handleSaveDetails = (title: string, description: string, language?: string) => {
    setEditingTitle(null)
    setEditingDescription(null)
    const patch: { id: string; title?: string; description?: string | null; language?: string } = { id: deck.id }
    if (title !== deck.title) patch.title = title
    if ((description || null) !== deck.description) patch.description = description || null
    if (language && language !== deck.language) patch.language = language
    if (patch.title === undefined && patch.description === undefined && patch.language === undefined) return
    updateDeck.mutate(patch, { onError: (err) => showToast(extractErrorMessage(err, dd.toastEditDetailsError)) })
  }

  // The phone's in-place language control. The server stores the code and
  // unlinks the deck's cards from the dictionary (ADR-070 Amendment 2); no
  // side changes. A refusal puts the control back by itself (the cached deck
  // is unchanged) and says so.
  const handleChangeLanguage = (language: string) => {
    updateDeck.mutate(
      { id: deck.id, language },
      { onError: (err) => showToast(extractErrorMessage(err, dd.deckLanguageError)) },
    )
  }

  const handleDeleteDeck = () => {
    deleteDeck.mutate(deck.id, { onSuccess: () => navigate(fromUrl ?? '/decks') })
  }

  const handleDeleteCard = (cardId: string) => {
    deleteCard.mutate({ id: cardId, deckId: deck.id }, {
      onSuccess: () => { setConfirmDeleteCardId(null) },
    })
  }

  // ⌘F (brief §22): focuses the search box rather than opening anything —
  // it is always mounted whenever the desktop panels are (both live inside
  // the same `cards.length > 0` guard), so there is no state to flip first.
  function handleSearchInDeck() {
    desktopSearchInputRef.current?.focus()
  }

  /**
   * A card created from an insert divider has just been POSTed, and the server
   * appended it at `max(position)+1` over the whole deck — which is what makes
   * the POST correct on a partially loaded list. One relative move then brings
   * it to the divider the reader pressed.
   *
   * Anchoring on the neighbour it will sit next to is exact whether or not the
   * rest of the deck is loaded; an absolute index computed over the loaded
   * window would not be (ADR-067 Decision 6). The dividers are rendered only
   * while no search is running, so `index` is a deck index and an index into
   * `cards` at the same time.
   */
  // A `const` rather than a hoisted `function`, so `deck`'s narrowing above
  // reaches it — the same shape `handleSaveCard` uses.
  const placeCardAt = (newCard: Card, index: number) => {
    const desired = [...cards]
    desired.splice(index, 0, newCard)
    const placement = relativePlacement(desired.map((c) => c.id), newCard.id)
    if (!placement) return
    moveCard.mutate(
      { deckId: deck.id, cardId: newCard.id, placement },
      // The card exists either way — only its position is in doubt, and
      // saying so beats a bare "save failed" the save disproves.
      { onError: () => showToast(dd.toastReorderFailed) },
    )
  }


  // Synonym/antonym chip click — switches panel to a free-floating term not tied to any card
  function handleSwitchTerm(word: string) {
    setReferenceCardId(null)
    setReferenceTerm(word)
  }

  const backLabel = deckBackLabel(fromUrl, deck.is_system, dd.backMyDecks, dd.backCatalog, dd.backMyCourses)

  // Panel stays open when referenceCardId is set OR when a free term is being shown (synonym lookup)
  const panelOpen = referenceCardId !== null || referenceTerm !== ''

  /**
   * The LIST COLUMN's head (§13: "list-scoped — the card-search field, the
   * shown-count, Add card — sits inside the list column, at exactly its
   * width"). Built here because every control in it is the page's own, and
   * handed to `DeckDesktopPanels`, which is the only component that knows
   * where the list column's edges are.
   *
   * The search field used to span both columns, above the split. That is the
   * case §13 names: a control that filters only the rows, drawn at the width
   * of the rows AND the pane, reads as belonging to the pane as well.
   *
   * The head line itself is one row — search, count, `Add card`. Under it sit
   * the narrowings that are also the list's own and not the deck's: the chip
   * filter, the selection toolbar, the anchor notice. They are part of the
   * head, so the list's scroll region starts under whichever of them are up,
   * and none of them ever scrolls.
   */
  // The head's first row when the deck can have texts (§19.6): the segment switch
  // and, under «Тексты», «Добавить текст» in the place the add-card control holds
  // under «Карточки». A row of its own, so the card head below it is unchanged.
  const desktopSwitchRow = showTextsSwitch ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: DECK_COLUMN_HEAD_H }}>
      <DeckSegmentSwitch compact cardCount={deck.card_count} textCount={textCount} segment={shownSegment} onSelect={selectSegment} />
      {showTexts && (
        <>
          <div style={{ flex: 1 }} />
          <button
            onClick={addTextToDeck}
            data-testid="deck-add-text"
            style={{ ...secondaryBtnStyle, height: DECK_COLUMN_HEAD_H, minHeight: 0, padding: '0 14px', fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Plus size={14} strokeWidth={2.2} aria-hidden />
            {tr.deckTexts.addText}
          </button>
        </>
      )}
    </div>
  ) : null

  const desktopListHead = (
    <>
      {desktopSwitchRow && <div style={{ marginBottom: 10 }}>{desktopSwitchRow}</div>}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', height: DECK_COLUMN_HEAD_H }}>
        <div style={{ flex: 1, minWidth: 0, position: 'relative', height: '100%' }}>
          <svg style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input
            ref={desktopSearchInputRef}
            value={cardSearch}
            onChange={(e) => setCardSearch(e.target.value)}
            placeholder={dd.searchCardsPlaceholder}
            style={{ width: '100%', height: '100%', boxSizing: 'border-box', padding: '0 12px 0 34px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 14, color: 'var(--text-primary)', outline: 'none' }}
            onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
            onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
          />
        </div>
        {/* How many cards the rows below are drawn from — deck-wide, and on the
            head line rather than in a strip of its own under it, which is what
            the search-result count used to be. */}
        {shownCount && (
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {shownCount}
          </span>
        )}
        {/* Absent on a system deck, and on any deck this reader does not own —
            §13: "a system deck has ... no Add card in the column head". */}
        {isOwner && (
          <button
            onClick={requestNewCard}
            style={{ ...secondaryBtnStyle, height: DECK_COLUMN_HEAD_H, minHeight: 0, padding: '0 14px', fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {dd.addCard}
          </button>
        )}
      </div>

      {/* The chip filter's own affordance (§16), summoned from ⋯ › Manage —
          the same component the feed renders, with the same server-side
          counts. Hidden while a search is running: the two are alternatives,
          not layers (the feed makes the same call by dropping its chip for
          the duration of a search). */}
      {desktopFilterOpen && !desktopSearching && (
        <div style={{ marginTop: 10, borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border)' }}>
          <DeckFilterChipsBar
            chip={desktopChip}
            onChipChange={setDesktopChip}
            counts={deckCounts ? {
              all: deckCounts.total,
              'no-translation': deckCounts.no_translation,
              'no-example': deckCounts.no_example,
              'not-filled': deckCounts.not_filled,
            } : null}
            // Closing the bar hides the control, not the filter — same rule
            // as the feed's, so a chip set here goes on narrowing the list.
            // What clears it is picking `All`.
            onClose={() => setDesktopFilterOpen(false)}
          />
        </div>
      )}

      {/* A running Fill-all batch — a slim line, not an overlay, so the list
          stays usable while cards fill in place. It left the band with
          everything else deck-level (ADR-067, thirteenth amendment) and sits
          here rather than on the bar: it reports on the rows below it, which
          are what change as it runs, and it is drawn for the page's plane.
          The selection strip that used to sit here moved up into the bar,
          which the selection header takes over (same amendment). */}
      {desktopFillAll.batchStatus && (
        <div style={{ marginTop: 10, paddingTop: 10, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--bg-surface)' }}>
          <FillAllProgressBar
            status={desktopFillAll.batchStatus}
            stopping={desktopFillAll.stopping}
            onStop={desktopFillAll.stop}
          />
        </div>
      )}

      {/* The list no longer starts at the top of the deck — a deep link
          re-anchored it. Said plainly, with the way back beside it, in the
          same words the phone feed has always used (§21.3 buys the single
          request by moving the window, and a moved window the reader can
          neither see nor undo is a worse trade than the chain). Hidden
          during a search, which answers over the whole deck and is not
          anchored at all. */}
      {!desktopSearching && (
        <DeckAnchorNotice
          anchorPage={anchorPage}
          pageSize={PAGE_SIZE}
          onBackToStart={() => moveAnchor(1)}
        />
      )}
    </>
  )

  /**
   * What the list's scroll region shows instead of rows. These used to replace
   * the entire two-pane shell, which took the search field down with them —
   * typing a word that matches nothing removed the box it had been typed into,
   * and the pane went with it. Now only the rows are replaced.
   */
  const desktopListPlaceholder = desktopSearchStatus === 'loading' ? (
    <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)', fontSize: 14 }}>
      {dm.searchRunning}
    </div>
  ) : desktopSearchStatus === 'error' ? (
    /* A failed request, said as one. Falling back to filtering the loaded
       pages would tell the reader their word is not in the deck in the one
       case where nothing here knows whether it is. */
    <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--danger)', fontSize: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <span>{dm.searchFailed}</span>
      <button
        onClick={() => desktopSearch.refetch()}
        style={{ ...secondaryBtnStyle, minHeight: 36, fontSize: 13 }}
      >
        {dm.searchRetry}
      </button>
    </div>
  ) : desktopSearchStatus === 'empty' ? (
    <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)', fontSize: 14 }}>
      {dd.noCardsMatch(cardSearch)}
    </div>
  ) : !desktopSearching && displayedCards.length === 0 && cards.length > 0 ? (
    // The chip narrowed the loaded window to nothing. Said as the filter it
    // is, not as an empty deck — the deck plainly has cards, which is also why
    // this branch is guarded on that: an EMPTY deck whose reader has just
    // pressed `Add first card` also has nothing to show, and telling them a
    // filter hid it would be a lie standing exactly where their new card
    // belongs.
    <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)', fontSize: 14 }}>
      {dm.noCardsMatchFilter}
    </div>
  ) : null

  return (
    <>
      <style>{`
        @media(max-width:480px){
          .card-row-move-buttons{display:none!important}
        }
      `}</style>
      {/* ── Mobile: the one pinned element on the screen (ADR-067 Decision 1).
             With the global bottom nav gone inside a deck, this strip is the
             only exit besides the device back gesture, so it never scrolls. */}
      {!isDesktop && (
        <>
          {/* Unmounted rather than merely covered while a selection is active
              — DeckMobileFeed takes over this exact fixed slot with the
              selection header instead (Prompt B). Two components claiming
              the same pinned band would leave the inert back button sitting
              underneath the replacement. */}
          {!isAnySelected && (
            <DeckTopStrip
              title={deck.title}
              modeWord={deck.is_system ? dd.systemBadge : (proofreadMode ? dd.modeWordEditing : undefined)}
              backLabel={backLabel}
              onBack={() => navigate(deckBackDest(fromUrl, deck.is_system))}
              // Search's single always-visible control (brief §15, 2026-08-04
              // rework) — present regardless of ownership or `deck.is_system`,
              // since the top strip itself already renders unconditionally in
              // both. This is the fix for "missing entirely for system decks":
              // the old entry point lived in the owner-only ⋯ menu, and a
              // system deck's viewer is never its owner.
              onToggleSearch={() => {
                // Captured here, synchronously, before the state flip that
                // collapses the header below — the click handler is the last
                // point with the pre-collapse DOM still in place. Only on the
                // opening edge: closing has nothing new to capture, and would
                // overwrite the value the effect in DeckMobileFeed still needs.
                if (!mobileSearchOpen) {
                  const scroller = getAppScrollElement()
                  preSearchScrollTopRef.current = scroller ? scroller.scrollTop : null
                }
                setMobileSearchOpen((o) => !o)
              }}
              searchOpen={mobileSearchOpen}
              // Search is the card list's; under «Тексты» there is nothing for it to search.
              searchDisabled={cards.length === 0 || showTexts}
            />
          )}
          {/* Reserves the strip's band at the top of the scrolling page — the
              strip (ordinary or selection variant) is out of flow, so without
              this the deck title would start underneath it. Both variants
              share the same height, so the reservation itself never changes. */}
          <div aria-hidden style={{ height: DECK_TOP_STRIP_CLEARANCE, flexShrink: 0 }} />
        </>
      )}

      {/* ── Desktop: the screen-header bar's content. A component rather than
             a hook call up top, because the selection header it swaps to
             needs the bulk-action handlers, and those live below the early
             returns. */}
      {isDesktop && (
        <DeckDesktopHeader
          deck={deck}
          backLabel={deckBackLabel(fromUrl, deck.is_system, dd.parentMyDecks, dd.parentCatalog, dd.parentMyCourses)}
          onBack={() => navigate(deckBackDest(fromUrl, deck.is_system))}
          isOwner={isOwner}
          canEditCards={headerCanEditCards}
          deckEditMode={headerCanEditCards && deckEditMode}
          // The mode acts on cards: pressed while «Тексты» is shown, it brings the
          // card list back first (§19.6).
          onSetDeckEditMode={(on) => { if (on) setSegment('cards'); setDeckEditMode(on) }}
          hasCards={cards.length > 0}
          narrow={narrowPanels}
          dailyPending={toggleLearning.isPending}
          onToggleDaily={() => toggleLearning.mutate(deck.id)}
          onStudyMemory={() => navigate(`/decks/${deck.id}/learn`, { state: { from: `/decks/${deck.id}` } })}
          onStudyCards={() => navigate(`/decks/${deck.id}/study`)}
          copyPending={copyDeck.isPending}
          onCopyToLibrary={() => copyDeck.mutate(
            { deckId: deck.id, isActiveForLearning: false },
            { onSuccess: () => { showToast(dd.toastCopiedToLibrary); navigate(libraryHref('mine')) } },
          )}
          menu={{
            open: showMoreMenu,
            onToggle: () => setShowMoreMenu((v) => !v),
            onClose: () => setShowMoreMenu(false),
            notFilledCount,
            cardsTotal: cardCounts.data?.total,
            canReorder: !desktopFilterActive && cards.length >= 2,
            filterActive: desktopFilterActive,
            exporting,
            updateDeckPending: updateDeck.isPending,
            onQuickPrep: () => { setShowMoreMenu(false); navigate(`/decks/${deck.id}/learn`, { state: { from: fromUrl ?? `/decks/${deck.id}`, is_prep: true } }) },
            onResetMastered: () => { setShowMoreMenu(false); setConfirmResetDeck(true) },
            onEditDetails: () => { setShowMoreMenu(false); setEditingTitle(deck.title); setEditingDescription(deck.description ?? '') },
            // The filter bar is the card list's: shown under «Тексты» it would be invisible.
            onFilterCards: () => { setShowMoreMenu(false); setSegment('cards'); setDesktopFilterOpen(true) },
            onFillEmptyCards: () => { setShowMoreMenu(false); setDesktopFillAllOpen(true) },
            onReorderCards: () => { setShowMoreMenu(false); setReorderScreenOpen(true) },
            onAddFromText: () => { setShowMoreMenu(false); navigate('/text-import', { state: { targetDeckId: deck.id, targetDeckTitle: deck.title } }) },
            onImportCsv: () => { setShowMoreMenu(false); setShowImport(true) },
            onExportCsv: () => { setShowMoreMenu(false); handleExport() },
            onTogglePublic: () => { setShowMoreMenu(false); handleTogglePublic() },
            onCopyShareLink: () => { setShowMoreMenu(false); handleCopyShareLink() },
            onDelete: () => { setShowMoreMenu(false); setConfirmDeleteDeck(true) },
            onStudyCards: () => { setShowMoreMenu(false); navigate(`/decks/${deck.id}/study`) },
          }}
          selection={{
            active: isAnySelected,
            count: selectedCount,
            selectableTotal: desktopSelectableTotal,
            wholeDeck: wholeDeckSelected,
            countsUnavailable,
            onSelectAll: handleSelectAll,
            onDeselectAll: handleDeselectAll,
            fillStatus: desktopFillSelection.error
              ? { tone: 'error', text: desktopFillSelection.error }
              : desktopFillSelection.progress
                ? { tone: 'muted', text: dm.fillingSelected(desktopFillSelection.progress.done, desktopFillSelection.progress.total) }
                : desktopFillSelection.resolving ? { tone: 'muted', text: dm.fillResolving } : null,
            onFill: desktopFillSelection.open,
            onMove: () => { setTransferMode('move'); setShowTransferModal(true) },
            onCopy: () => { setTransferMode('copy'); setShowTransferModal(true) },
            onDelete: () => setConfirmBulkDelete(true),
          }}
        />
      )}
      {isDesktop && isOwner && editingTitle !== null && (
        <DeckDetailsDialog
          initialTitle={editingTitle}
          initialDescription={editingDescription ?? ''}
          language={{
            initial: deck.language,
            labels: { label: dd.deckLanguageLabel, note: dd.deckLanguageNote },
          }}
          labels={{
            heading: dd.detailsDialogTitle,
            name: dd.detailsNameLabel,
            description: dd.detailsDescLabel,
            descriptionPlaceholder: dd.addDescPlaceholder,
            cancel: dd.cancelLabel,
            save: dd.detailsSave,
          }}
          saving={updateDeck.isPending}
          onCancel={() => { setEditingTitle(null); setEditingDescription(null) }}
          onSave={handleSaveDetails}
        />
      )}

      {/* No maxWidth/margin of its own — Layout picks this route's page
          container width (var(--page-max-width-wide), since this is the
          deck screen) and already caps and centers it. This div stays a
          flex row purely to seat the reference-panel column beside the
          main content below, not to duplicate that width decision. */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      {/* The deck column. On desktop it is the screen's FRAME: a fixed box the
          height of the scrollport's content area, divided between the band
          (which does not scroll) and the two columns (which do, each on its
          own). Nothing here scrolls the page, so the band and the two column
          heads cannot drift out of register — §13. On mobile it is an ordinary
          flow column and the page scrolls, exactly as before. */}
      <div data-deck-frame style={deckFrame !== null
        ? {
            flex: 1, minWidth: 0, boxSizing: 'border-box',
            height: deckFrame.height, marginTop: deckFrame.marginTop, marginBottom: deckFrame.marginBottom,
            paddingTop: deckFrame.paddingTop, display: 'flex', flexDirection: 'column',
          }
        : { flex: 1, minWidth: 0 }}>

      {/* ── Scrolling header (§4): everything above the list except the pinned
          48 px strip — identity, Study CTAs, the daily-learning toggle. Hidden
          outright while mobile search is open (brief §15) rather than merely
          scrolled past: with search's own scroll motion bringing the feed
          flush under the panel (see the effect in DeckMobileFeed), a header
          still present would just be more of the page that motion has to
          cross, and the reader could still scroll back up into it — exactly
          the dead space §15's 2026-08-04 rework was meant to remove. A plain
          conditional render, not a second scroll writer: DeckMobileFeed's own
          effect (keyed on the same `searchOpen`) does the one motion that
          moves the page, both on the way in and on the way back out. Desktop
          is unaffected — `mobileSearchOpen` is never set true there, since
          the control that flips it only renders inside `!isDesktop`. */}
      {!isDesktop && !mobileSearchOpen && (
      // ── The deck-scoped band (§13: "a control spans the width of what it
      //    acts on"). Identity and its chips, the description, the study
      //    actions, the daily-study row — every one of them about the DECK, so
      //    every one of them spans the page. What acts on the LIST left this
      //    band entirely and now sits in the list column's own head; the
      //    card-search field spanning both columns was the case §13 names. On
      //    desktop the band is a fixed share of the frame and does not scroll
      //    — hence the measurement, which is what sizes the columns below.
      //    `data-deck-band` / `data-deck-columns` (and the three in
      //    `DeckDesktopPanels`) name the boxes this screen's height chain is
      //    stated in, so a browser check can measure the chain instead of
      //    guessing at a DOM shape — the same kind of hook as `data-card-id`.
      <div data-deck-band>
      {/* ── Compact utility header ── */}
      <div style={{ marginBottom: 20 }}>
        {/* Back affordance — mobile only; it lives in the pinned strip above
            on mobile, which must not scroll away, and in the desktop
            screen-header bar (see the useScreenHeader call above), never in
            both places. */}

        {/* Cover + identity + overflow */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <DeckCover deckId={deck.id} title={deck.title} isSystem={deck.is_system} size="list" style={{ marginTop: 2, flexShrink: 0 }} />

          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Title — editable via ··· › Edit details. Desktop: the static
                display is hidden here (the screen-header bar shows it
                instead) except while actively editing, since editing happens
                in place rather than in the bar. Mobile: always shown, same
                as before this rework. */}
            {isOwner && editingTitle !== null ? (
              <input
                autoFocus
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                onBlur={handleSaveTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle()
                  if (e.key === 'Escape') setEditingTitle(null)
                }}
                maxLength={300}
                style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', background: 'var(--bg-secondary)', border: '1.5px solid var(--accent)', borderRadius: 'var(--radius-sm)', padding: '2px 8px', outline: 'none', width: '100%', fontFamily: 'var(--font-ui)', letterSpacing: '-0.01em' }}
              />
            ) : (
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em', lineHeight: 1.3, wordBreak: 'break-word' }}>
                {deck.title}
              </h1>
            )}

            {/* Meta line: N cards · N due · N% learned */}
            <div style={{ marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {dd.cardCount(deck.card_count)}
              </span>
              {deck.due_count !== null && deck.due_count > 0 && (
                <span style={{ fontSize: 13, color: 'var(--warning)', fontWeight: 600 }}>
                  {dd.dueBadge(deck.due_count)}
                </span>
              )}
              {deck.card_count > 0 && (
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {dd.learnedPct(Math.round((deck.learned_count / deck.card_count) * 100))}
                </span>
              )}
              {deck.cefr_level && <CEFRBadge level={deck.cefr_level} />}
              {deck.is_system && (
                <span style={{ fontSize: 11, padding: '2px 8px', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', borderRadius: 'var(--radius-pill)', fontWeight: 600, border: '1px solid var(--border)', letterSpacing: '0.04em', textTransform: 'uppercase' as const }}>
                  {dd.systemBadge}
                </span>
              )}
              {deck.is_public && !deck.is_system && (
                <span style={{ fontSize: 11, padding: '2px 8px', background: 'var(--accent-soft)', color: 'var(--accent)', borderRadius: 'var(--radius-pill)', fontWeight: 600 }}>
                  {dd.publicBadge}
                </span>
              )}
              {!isOwner && !deck.is_system && (
                <span style={{ fontSize: 11, padding: '2px 8px', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', borderRadius: 'var(--radius-pill)', fontWeight: 500, border: '1px solid var(--border)' }}>
                  {dd.readOnlyBadge}
                </span>
              )}
            </div>

            {/* Description — editable via ··· › Edit details; no edit placeholder shown in view mode */}
            {isOwner && editingDescription !== null ? (
              <textarea
                // Not autoFocus: the title input above opens at the same time
                // (same "Edit details" action) and also carries autoFocus. Two
                // autofocused elements mounting in one commit race for focus —
                // the browser resolves it to whichever is later in the DOM,
                // stealing focus here and firing a blur on the title input
                // before the person can type into it. handleSaveTitle's
                // unconditional close-on-blur then discarded the title editor
                // on every open, which is why renaming silently did nothing.
                // Title is the field this entry point exists to serve, so it
                // gets the sole deterministic autofocus; description is still
                // one tap away.
                value={editingDescription}
                onChange={(e) => setEditingDescription(e.target.value)}
                onBlur={handleSaveDescription}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setEditingDescription(null)
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveDescription() }
                }}
                rows={2}
                placeholder={dd.addDescPlaceholder}
                style={{ display: 'block', marginTop: 6, fontSize: 13, color: 'var(--text-primary)', background: 'var(--bg-secondary)', border: '1.5px solid var(--accent)', borderRadius: 'var(--radius-sm)', padding: '4px 8px', outline: 'none', resize: 'vertical', width: '100%', maxWidth: 560, fontFamily: 'inherit', lineHeight: 1.5 }}
              />
            ) : deck.description ? (
              /* The phone renders the description whole: its header scrolls
                 with the page, so it has no fixed height to protect (the
                 two-line clamp was the desktop band's rule, and left with the
                 band — ADR-067, thirteenth amendment). */
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)', maxWidth: 560, lineHeight: 1.5 }}>
                {deck.description}
              </p>
            ) : null}

            {/* Deck language: every viewer reads it; the owner of a user deck
                changes it. Phone only — the desktop shows it in the facts
                line and edits it in the details dialog. */}
            {!isDesktop && (
              <DeckLanguageField
                value={deck.language}
                editable={isOwner && !deck.is_system}
                onChange={handleChangeLanguage}
                disabled={updateDeck.isPending}
                labels={{ label: dd.deckLanguageLabel, note: dd.deckLanguageNote }}
              />
            )}
          </div>

          {/* ··· deck-level overflow menu — mobile only. Desktop's equivalent
              lives in the screen-header bar (DeckHeaderOverflowMenu, wired
              via the useScreenHeader call above), never rendered here too. */}
          {!isDesktop && (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setShowMoreMenu((v) => !v)}
              aria-label={dd.moreOptionsLabel}
              aria-expanded={showMoreMenu}
              aria-haspopup="menu"
              style={{ ...secondaryBtnStyle, padding: '8px 10px', minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <MoreHorizontal size={18} />
            </button>
            {showMoreMenu && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setShowMoreMenu(false)} />
                <div
                  role="menu"
                  style={isDesktop ? {
                    position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100,
                    background: 'var(--bg-surface)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)',
                    minWidth: 240, overflow: 'hidden',
                  } : {
                    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
                    background: 'var(--bg-surface)', borderTop: '1px solid var(--border)',
                    borderRadius: '20px 20px 0 0',
                    boxShadow: '0 -8px 30px rgba(0,0,0,0.12)',
                    paddingBottom: 'env(safe-area-inset-bottom)',
                    maxHeight: '80vh', overflowY: 'auto' as const,
                  }}
                >
                  {!isDesktop && (
                    <div aria-hidden style={{ width: 36, height: 4, background: 'var(--border)', borderRadius: 2, margin: '12px auto 8px' }} />
                  )}

                  {/* DECK group — owner only */}
                  {isOwner && (
                    <OverflowMenuGroup label={dd.menuDeckGroup}>
                      <OverflowMenuItem
                        label={dd.menuEditDetails}
                        sublabel={dd.menuEditDetailsSub}
                        onClick={() => { setShowMoreMenu(false); setEditingTitle(deck.title); setEditingDescription(deck.description ?? '') }}
                      />
                    </OverflowMenuGroup>
                  )}

                  {/* MANAGE group — owner only (Prompt 8), and on BOTH surfaces
                      since 2026-08-08. It used to be mobile-only on the reading
                      that desktop "already exposes filter/Fill-all inline in its
                      own chrome"; desktop exposed neither, so the whole
                      management layer — Filter, Reorder, Fill empty cards — was
                      simply absent there (brief §23). One group serves both:
                      each item summons the same component on either surface and
                      differs only in which state flag holds it open.

                      Search left this group in the 2026-08-04 rework — it now
                      has its own always-visible control (the top strip on
                      mobile, the search box above the list on desktop),
                      available to owner and non-owner alike (system decks
                      included), so it no longer belongs to an owner-only menu.
                      Filter stays here: it is still "their own separate
                      affordance" (brief §15), just no longer sharing this entry
                      with search. */}
                  {isOwner && (
                    <OverflowMenuGroup label={dm.menuManageGroup}>
                      <OverflowMenuItem
                        label={dm.menuFilterCards}
                        onClick={() => {
                          setShowMoreMenu(false)
                          // The filter chips are the card feed's: bring it back first.
                          setSegment('cards')
                          if (isDesktop) setDesktopFilterOpen(true)
                          else setMobileFilterOpen(true)
                        }}
                        disabled={cards.length === 0}
                      />
                      {/* The badge is the DECK's not-filled count, from the
                          server — the sheet this opens quotes the same number,
                          so the menu must not preview it with the loaded pages'
                          (brief §23 item 9). */}
                      <OverflowMenuItem
                        label={dm.menuFillEmptyCards}
                        sublabel={notFilledCount > 0 && cardCounts.data ? dm.resultCount(notFilledCount, cardCounts.data.total) : undefined}
                        onClick={() => {
                          setShowMoreMenu(false)
                          if (isDesktop) setDesktopFillAllOpen(true)
                          else {
                            // The phone's sheet is the card feed's: bring it back first.
                            setSegment('cards')
                            setMobileFillAllOpen(true)
                          }
                        }}
                        disabled={cards.length === 0}
                      />
                      {/* Reorder lives in the management layer and is refused
                          while a filter is on (brief §4.4): dragging within a
                          subset does not say where the card goes among the
                          cards the filter is hiding. The refusal is a plain
                          sentence in the sublabel rather than a silently dead
                          row — the reader is told what to do about it. The
                          desktop list carries a drag handle per row as well;
                          this is the same screen the phone gets, for the same
                          reason it exists there — a whole-deck arrangement,
                          loaded from card 1, where a row's number is its number
                          in the deck rather than in the loaded window. */}
                      <OverflowMenuItem
                        label={dr.menuReorderCards}
                        sublabel={filterNarrowingList ? dr.menuReorderFiltered : dr.menuReorderSub}
                        onClick={() => { setShowMoreMenu(false); setReorderScreenOpen(true) }}
                        disabled={filterNarrowingList || cards.length < 2}
                      />
                    </OverflowMenuGroup>
                  )}

                  {/* CONTENT group — owner only */}
                  {isOwner && (
                    <OverflowMenuGroup label={dd.menuContentGroup}>
                      <OverflowMenuItem
                        label={dd.menuAddFromText}
                        onClick={() => { setShowMoreMenu(false); navigate('/text-import', { state: { targetDeckId: deck.id, targetDeckTitle: deck.title } }) }}
                      />
                      <OverflowMenuItem
                        label={dd.menuImportCsv}
                        onClick={() => { setShowMoreMenu(false); setShowImport(true) }}
                      />
                      <OverflowMenuItem
                        label={exporting ? dd.menuExporting : dd.menuExportCsv}
                        onClick={() => { setShowMoreMenu(false); handleExport() }}
                        disabled={exporting}
                      />
                    </OverflowMenuGroup>
                  )}

                  {/* SHARING group — owner sharing controls + share link for all */}
                  {(isOwner || deck.share_token) && (
                    <OverflowMenuGroup label={dd.menuSharingGroup}>
                      {isOwner && (
                        <OverflowMenuItem
                          label={deck.is_public ? dd.menuMakePrivate : dd.menuMakePublic}
                          sublabel={deck.is_public ? dd.menuMakePrivateSub : dd.menuMakePublicSub}
                          onClick={() => { setShowMoreMenu(false); handleTogglePublic() }}
                          disabled={updateDeck.isPending}
                        />
                      )}
                      {deck.share_token && (
                        <OverflowMenuItem
                          label={dd.menuCopyShareLink}
                          onClick={() => { setShowMoreMenu(false); handleCopyShareLink() }}
                        />
                      )}
                    </OverflowMenuGroup>
                  )}

                  {/* STUDY group — any reader with study history in this deck,
                      system decks included (ADR-098): the progress is theirs. */}
                  {deck.learned_count > 0 && (
                    <OverflowMenuGroup label={dd.menuStudyGroup}>
                      <OverflowMenuItem
                        label={tr.masteryReset.deckMenuItem}
                        sublabel={tr.masteryReset.deckMenuItemSub}
                        onClick={() => { setShowMoreMenu(false); setConfirmResetDeck(true) }}
                      />
                    </OverflowMenuGroup>
                  )}

                  {/* System deck: copy to personal library */}
                  {deck.is_system && (
                    <>
                      <OverflowMenuSeparator />
                      <OverflowMenuItem
                        label={copyDeck.isPending ? dd.menuCopying : dd.menuCopyToLibrary}
                        sublabel={dd.menuCopyToLibrarySub}
                        onClick={() => {
                          setShowMoreMenu(false)
                          copyDeck.mutate(
                            { deckId: deck.id, isActiveForLearning: false },
                            { onSuccess: () => { showToast(dd.toastCopiedToLibrary); navigate(libraryHref('mine')) } },
                          )
                        }}
                        disabled={copyDeck.isPending}
                      />
                    </>
                  )}

                  {/* Destructive zone — visually separated */}
                  {isOwner && !deck.is_inbox && (
                    <>
                      <OverflowMenuSeparator spacious />
                      <OverflowMenuItem
                        label={dd.menuDeleteDeck}
                        onClick={() => { setShowMoreMenu(false); setConfirmDeleteDeck(true) }}
                        destructive
                      />
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          )}
        </div>
      </div>

      {/* ── Study exercises ──
          Two exercises, not one action and two variants of it: memory recall
          (typed, the main path — carries the one accent fill this screen
          allows) and flashcard review (flip and read), side by side as
          ordinary buttons rather than full-width slabs. Quick prep left this
          row entirely — it isn't a third exercise, it's a filtered slice of
          "by memory" that only means something once the deck has been
          studied, so it now lives in the ⋯ menu, conditional on that history
          (see the useScreenHeader config above).
          Desktop: always shown (unchanged gate). Mobile: only once the deck
          has cards — an empty deck's mobile feed leads with its own creation
          entry points instead (DeckMobileFeed empty state). */}
      {cards.length > 0 && (
      // Ordinary buttons, sized to their own label — no flex:1, no width:
      // stretching either one to fill the row is exactly the "slab" this
      // replaced. The row is only as tall and as wide as the two buttons
      // need; whatever's left of the column stays empty beside them.
      <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
        <button
          onClick={() => navigate(`/decks/${deck.id}/learn`, { state: { from: `/decks/${deck.id}` } })}
          style={primaryBtnStyle}
        >
          {dd.learnByMemory}
        </button>
        <button
          onClick={() => navigate(`/decks/${deck.id}/study`)}
          style={secondaryBtnStyle}
        >
          {dd.learnByCards}
        </button>
      </div>
      )}

      {/* ── In daily learning toggle (compact) ── same gate as Study CTAs above. */}
      {cards.length > 0 && (
      <div style={{ marginBottom: 20, padding: '10px 14px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
        <button
          onClick={() => toggleLearning.mutate(deck.id)}
          disabled={toggleLearning.isPending}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%', minHeight: 40 }}
        >
          <LearningToggleSwitch active={deck.is_active_for_learning} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1.3 }}>{dd.inDailyLearning}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 1 }}>{dd.inDailyLearningDesc}</div>
          </div>
        </button>
      </div>
      )}

      </div>
      )}

      {/* ── «Карточки | Тексты» on the phone (handoff §8): a tap-only switch in the
          scrolling content, under the daily-queue row — never in the thin top
          strip (ADR-067 Decision 1), and no swipe between the two (ADR-115
          Decision 11). Hidden while search is open, while cards are selected and
          in editing mode: those are card modes, and the list they act on must not
          be swapped out from under them. */}
      {!isDesktop && !mobileSearchOpen && showTextsSwitch && !proofreadMode && !isAnySelected && (
        <div data-testid="deck-segment-row" style={{ marginBottom: 12 }}>
          <DeckSegmentSwitch cardCount={deck.card_count} textCount={textCount} segment={shownSegment} onSelect={selectSegment} />
        </div>
      )}

      {/* ── Card section ──
          Desktop: unchanged, exact prior markup. Mobile: the chat-style card
          feed (deck-feed-v2 shell) replaces the flat list + empty state. */}
      {!isDesktop ? (
        <>
        {showTexts ? (
          <DeckTextsMobile
            deck={{ id: deck.id, title: deck.title }}
            hasCards={deck.card_count > 0}
            // Same destination as the feed's own bar below.
            onStudy={() => navigate(`/decks/${deck.id}/learn`, { state: { from: `/decks/${deck.id}` } })}
          />
        ) : (
        <DeckMobileFeed
          deckId={deck.id}
          deckLanguage={deck.language}
          cards={cards}
          // The deck's own count, not the loaded page count — §13's counter
          // must say how big the deck is (brief §23 item 9).
          deckCardCount={deck.card_count}
          isOwner={isOwner}
          cardsLoading={cardsLoading}
          hasNextPage={!!hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          // The empty deck's import entrance. Carries the deck target, exactly
          // as the overflow menu's "Add words from text" does: TextImportPage
          // reads `targetDeckId` to force its "existing deck" mode with this
          // deck already selected. Without the state it defaulted to "new
          // deck" mode, so the one entrance offered for filling THIS deck
          // would have created a second one beside it.
          onPasteNote={() => navigate('/text-import', { state: { targetDeckId: deck.id, targetDeckTitle: deck.title } })}
          // Same destination and state as the in-header "Learn this deck" CTA
          // above — the pinned bar is a second route to it, not a variant.
          onStudy={() => navigate(`/decks/${deck.id}/learn`, { state: { from: `/decks/${deck.id}` } })}
          editingMode={proofreadMode}
          // Offered only to an owner — decided inside the feed, with `+ Card`.
          onEnterEditing={() => {
            setSelection(EMPTY_SELECTION)
            setProofreadMode(true)
          }}
          onExitEditing={exitEditingMode}
          pageSize={PAGE_SIZE}
          anchorPage={anchorPage}
          onAnchorChange={moveAnchor}
          searchOpen={mobileSearchOpen}
          // Stable, not an inline closure: the feed's deep-link arrival depends
          // on it, and a new identity every render would tear that effect's
          // listeners down mid-motion.
          onCloseSearch={closeMobileSearch}
          preSearchScrollTopRef={preSearchScrollTopRef}
          filterOpen={mobileFilterOpen}
          onCloseFilter={() => setMobileFilterOpen(false)}
          onFilterActiveChange={setMobileFilterActive}
          fillAllOpen={mobileFillAllOpen}
          onCloseFillAll={() => setMobileFillAllOpen(false)}
          selection={selection}
          setSelection={setSelection}
          deckCounts={deckCounts}
          countsUnavailable={countsUnavailable}
          onRequestMove={() => { setTransferMode('move'); setShowTransferModal(true) }}
          onRequestCopy={() => { setTransferMode('copy'); setShowTransferModal(true) }}
          onRequestDelete={() => setConfirmBulkDelete(true)}
          // The deep link, handed over rather than acted on here: the page
          // resolves WHICH card and where it sits in the window; the feed owns
          // the row element and the narrowings that can hide it.
          deepLinkCardId={targetCardId}
          deepLinkNonce={nonce}
          deepLinkWindowIndex={targetWindowIndex}
          deepLinkPending={deepLinkPending}
          highlightedCardId={highlight?.cardId ?? null}
          onDeepLinkArrived={markDeepLinkTarget}
        />
        )}
        {/* Its own screen, over everything (brief §4.4) — it loads the deck
            from page 1 itself rather than borrowing the feed's window, so the
            number on each row is the card's number in the deck. */}
        {reorderScreenOpen && (
          <DeckReorderScreen deckId={deck.id} onClose={() => setReorderScreenOpen(false)} />
        )}
        </>
      ) : (
      <>

          {/* «Тексты» (§19.6): the same frame, columns and floor as the cards, so
              the screen does not change shape when the segment does. */}
          {showTexts ? (
            <div data-deck-columns style={deckFrame !== null ? { flex: 1, minHeight: 0 } : undefined}>
              <DeckTextsPanels
                deck={{ id: deck.id, title: deck.title }}
                head={desktopSwitchRow}
                narrow={narrowPanels}
                onAdd={addTextToDeck}
              />
            </div>
          ) : (<>
          {/* A deck with no cards has no list head to carry the switch, and it can
              still have texts (an essay saved to it): the switch stands alone. */}
          {showTextsSwitch && !cardsLoading && cards.length === 0 && newCardNonce === 0 && (
            <div style={{ marginBottom: 16 }}>{desktopSwitchRow}</div>
          )}
          {/* Card list */}
          {cardsLoading ? (
            <CardsListSkeleton />
          ) : cards.length === 0 && newCardNonce === 0 ? (
            /* Empty own-deck state. It stands until the reader asks for a
               card — from then on the panels below are mounted and the right
               one holds the new card, which is the same surface every other
               creation path on this screen lands in (ADR-067 Decision F).
               There is no separate authoring surface for a first card. */
            <div style={{ textAlign: 'center', padding: '52px 0', color: 'var(--text-secondary)' }}>
              <div style={{ width: 48, height: 48, margin: '0 auto 14px', background: 'var(--bg-secondary)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-secondary)' }}>
                  <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </div>
              <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{dd.noCardsTitle}</p>
              <p style={{ margin: '0 0 20px', fontSize: 13 }}>{dd.noCardsDesc}</p>
              {isOwner && (
                <button
                  onClick={requestNewCard}
                  style={{ ...primaryBtnStyle, minHeight: 44 }}
                >
                  {dd.addFirstCard}
                </button>
              )}
            </div>
          ) : (
            <>
              {/* The two panels (brief §22, item 8). The list on the left, the
                  selected card on the right — opened for READING by default,
                  editing entered per card or, via `deckEditMode` below, per
                  deck (ADR-067 amendment, 2026-09-08).
                  The columns region: what the frame has left after the band.
                  It used to be a sticky box the height of the viewport's
                  content area, with the chrome above it scrolling away first —
                  which is exactly the band that §13 says must not scroll, and
                  the reason the two column heads could be dragged out of
                  register by one wheel event. `minHeight: 0` so the two
                  scrollers inside actually scroll rather than pushing this box
                  open. Until the first measurement lands the shell keeps its
                  natural height rather than a guessed one.
                  No bottom padding: the region, the pane and the list's
                  scroll region all run to the frame's bottom edge, and §13's
                  floor inset lives inside the two scrollers' own content
                  (`DeckDesktopPanels`) — a shortened box read as truncation. */}
              <div
                data-deck-columns
                ref={cardListRef}
                style={deckFrame !== null ? { flex: 1, minHeight: 0 } : undefined}
              >
                <DeckDesktopPanels
                  ref={panelsRef}
                  deckId={deck.id}
                  deckLanguage={deck.language}
                  cards={displayedCards}
                  // A card's number in the deck. The row logic keeps `indexOf`
                  // (a position in the loaded window); only what is printed
                  // reads this.
                  numberOf={numberOf}
                  windowStartsAtCard={(anchorPage - 1) * PAGE_SIZE + 1}
                  // A search match carries the server's own deck index; a card
                  // from the loaded window is looked up in the O(1) map built
                  // once per render (brief §21.3 item 4 — this was a findIndex
                  // per row, i.e. O(n²) for the whole list).
                  indexOf={(card) => (desktopSearching
                    ? (card as CardSearchItem).index
                    : cardIndexById.get(card.id) ?? -1)}
                  deckCardCount={deck.card_count}
                  isOwner={isOwner}
                  searching={desktopSearching}
                  onCardInsertedAt={placeCardAt}
                  onSaveError={showToast}
                  onFieldDeleted={({ label, restore }) =>
                    showFieldTransient({ kind: 'undo', label, restore }, 8000)}
                  // ADR-066 Decision 1: a reorder that changed which field of a
                  // repeated kind Learn prompts from is reported in words —
                  // moving a field is a change to what is studied, not tidying.
                  onFieldOrderChanged={(labels) =>
                    showFieldTransient({ kind: 'notice', message: cb.fieldPromptChangedMsg(labels.join(', ')) }, 7000)}
                  onSelectionChanged={clearFieldTransient}
                  onSearchInDeck={handleSearchInDeck}
                  pageOverlayOpen={overlayOpen}
                  onMoveCardToDeck={handleMoveSingleCard}
                  onDeleteCard={(cardId) => setConfirmDeleteCardId(cardId)}
                  referenceCardId={referenceCardId}
                  onShowReference={handleShowReference}
                  onCloseReference={closeReferencePanel}
                  referencePanel={panelOpen ? (
                    <WordReferencePanel
                      variant="column"
                      term={referenceTerm}
                      isOpen={panelOpen}
                      onClose={closeReferencePanel}
                      onSwitchTerm={handleSwitchTerm}
                      targetLanguage={currentUser?.target_language ?? 'en'}
                      sourceLanguage={currentUser?.source_language ?? 'en'}
                    />
                  ) : null}
                  highlightedCardId={highlight?.cardId ?? null}
                  // Row elements, registered as they mount. The deep-link
                  // motion above reads this instead of querying the document
                  // (ADR-067 Decision 6).
                  rowRegistry={rowRegistry}
                  isAnySelected={isAnySelected}
                  isSelected={(cardId) => isCardSelected(selection, cardId)}
                  onCheckboxClick={handleCheckboxClick}
                  onCheckboxPointerDown={handleCheckboxPointerDown}
                  onCheckboxPointerEnter={handleCheckboxPointerEnter}
                  loadMoreRef={loadMoreRef}
                  isFetchingNextPage={listFetchingNext}
                  narrow={narrowPanels}
                  // Gated on the same `headerCanEditCards` the header badge
                  // and the menu entry use, so a system deck (or a deck this
                  // reader does not own) can never be handed a mode it has no
                  // way to turn off.
                  deckEditMode={headerCanEditCards && deckEditMode}
                  newCardNonce={newCardNonce}
                  // The list column's own head and, when there are no rows to
                  // show, what stands in for them — both built above, both
                  // placed by the panels, which own the column's edges.
                  listHead={desktopListHead}
                  listPlaceholder={desktopListPlaceholder}
                  // What is narrowing the rows right now, so the pane can tell
                  // "your card is hidden, lift this" from "your card is gone".
                  // The page owns both narrowings; the panel owns neither and
                  // cannot tell them apart from the rows it is handed.
                  listNarrowing={desktopSearching
                    ? { kind: 'search', query: desktopQuery }
                    : desktopFilterActive ? { kind: 'filter' } : null}
                  deckDescription={deck.description}
                  onPasteWordList={(lines) => navigate('/text-import', {
                    state: {
                      prefillText: lines.join('\n'),
                      targetDeckId: deck.id,
                      targetDeckTitle: deck.title,
                    },
                  })}
                />
              </div>
            </>
          )}
          </>)}
        </>
      )}

      {/* ── Modals ── */}
      {/* The confirm names the count the delete will actually apply to, and says
          so without a number rather than guessing when it is not yet known. */}
      {isOwner && confirmBulkDelete && <ConfirmModal title={selectedCount === null ? dd.deleteBulkTitlePending : dd.deleteBulkTitle(selectedCount)} message={dd.deleteBulkMsg} onCancel={() => setConfirmBulkDelete(false)} onConfirm={handleBulkDelete} loading={deleteCards.isPending} />}
      {isOwner && confirmDeleteCardId && (
        <ConfirmModal
          title={dd.deleteCardTitle}
          message={dd.deleteCardMsg}
          onCancel={() => setConfirmDeleteCardId(null)}
          onConfirm={() => handleDeleteCard(confirmDeleteCardId)}
          loading={deleteCard.isPending}
        />
      )}
      {confirmResetDeck && (
        <DeckResetConfirm
          deckId={deck.id}
          onClose={() => setConfirmResetDeck(false)}
          onDone={(n) => { setConfirmResetDeck(false); showToast(tr.masteryReset.deckDone(n)) }}
        />
      )}
      {isOwner && confirmDeleteDeck && <ConfirmModal title={dd.deleteDeckTitle} message={dd.deleteDeckMsg(deck.title, deck.card_count)} onCancel={() => setConfirmDeleteDeck(false)} onConfirm={handleDeleteDeck} loading={deleteDeck.isPending} />}
      {isOwner && showTransferModal && (
        <DeckPickerModal
          mode={transferMode}
          sourceDeckId={deck.id}
          selection={toSelectionRequest(selection)}
          selectionCount={selectedCount}
          // Cancel (and Android back, via the sentinel below) abandons the
          // DESTINATION choice, not the selection underneath it — the sheet is
          // its own mode nested inside multi-select, not multi-select's exit.
          // Only a completed operation (`onSuccess`) clears `selection`,
          // because only then are the cards it named actually gone from — or
          // duplicated out of — this deck.
          onClose={() => setShowTransferModal(false)}
          onSuccess={({ count, targetDeckId, targetDeckTitle, resultCardIds, originalOrder, undoToken }) => {
            setShowTransferModal(false)
            setSelection(EMPTY_SELECTION)
            dismissUndo()
            // The source deck's order comes from the SERVER, captured before
            // the move ran, already in ascending order and already anchored on
            // neighbours. It used to be derived here from `allCards` — the
            // pages this page had fetched — with every card it had not loaded
            // dropped by the `idx === -1` filter. On a whole-deck move that is
            // most of them, so Undo restored the fragment this session held and
            // reported a completed undo (brief §23 item 9, same root cause).
            //
            // The copy's Undo carries the server's token for that one
            // operation, not `resultCardIds` — those ids named the cards by
            // where they came from, so they were the same for every copy of
            // those cards into that deck and an Undo acting on them deleted an
            // earlier copy's work. No token (an older server, or a copy that
            // created nothing) means no offer rather than a broken one.
            if (transferMode === 'copy' && !undoToken) return
            const pending: UndoPending = transferMode === 'move'
              ? { mode: 'move', count, destDeckId: targetDeckId, destDeckTitle: targetDeckTitle, cardIds: resultCardIds, sourceDeckId: deck.id, originalOrder }
              : { mode: 'copy', count, destDeckId: targetDeckId, destDeckTitle: targetDeckTitle, undoToken: undoToken as string }
            setUndoPending(pending)
            scheduleUndoDismiss(pending.mode)
          }}
        />
      )}

      <CSVImportModal isOpen={showImport} onClose={() => setShowImport(false)} deckId={deck.id} deckTitle={deck.title} />

      {/* ── Desktop management chrome summoned from ⋯ › Manage ──
          Both sheets are the very components the phone shows — the disclosure a
          reader is asked to act on is one implementation, not one per surface
          (brief §23 constraint). */}
      {isOwner && isDesktop && desktopFillAllOpen && (
        <FillAllSheet
          preflight={desktopFillAll.preflight}
          loading={desktopFillAll.loading}
          error={desktopFillAll.preflightError}
          starting={desktopFillAll.starting}
          onConfirm={desktopFillAll.confirm}
          onCancel={() => setDesktopFillAllOpen(false)}
          layout="dialog"
        />
      )}
      {isOwner && isDesktop && desktopFillSelection.sheetOpen && (
        <FillAllSheet
          preflight={desktopFillSelection.preflight}
          loading={desktopFillSelection.loading}
          error={desktopFillSelection.preflightError}
          starting={false}
          onConfirm={desktopFillSelection.confirm}
          onCancel={desktopFillSelection.cancel}
          nothingToFillMessage={dm.fillSelectionNothingToFill}
          layout="dialog"
        />
      )}
      {/* Its own screen, over everything (brief §4.4) — it loads the deck from
          page 1 itself rather than borrowing the list's window, so the number on
          each row is the card's number in the deck. Same component the phone
          opens; its drag already runs on a PointerSensor, so a mouse drives it
          unchanged. */}
      {isOwner && isDesktop && reorderScreenOpen && (
        <DeckReorderScreen deckId={deck.id} onClose={() => setReorderScreenOpen(false)} />
      )}

      </div>{/* end main content */}

      </div>{/* end flex container */}

      {/* Mobile: modal panel (< 768px) */}
      {!isDesktop && (
        <WordReferencePanel
          variant="modal"
          term={referenceTerm}
          isOpen={panelOpen}
          onClose={closeReferencePanel}
          onSwitchTerm={handleSwitchTerm}
          targetLanguage={currentUser?.target_language ?? 'en'}
          sourceLanguage={currentUser?.source_language ?? 'en'}
        />
      )}

      {/* Field-level transient from the desktop right panel: what was deleted
          with the way back, or what a reorder changed about what Learn asks.
          Above the bulk bar's own slot rather than in it — the two can be up at
          once and neither may cover the other. */}
      <AnimatePresence>
        {fieldTransient && isDesktop && (
          <motion.div
            key="field-transient"
            role="status"
            aria-live="polite"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.18 }}
            style={{
              position: 'fixed', bottom: 84, left: '50%', transform: 'translateX(-50%)',
              background: 'var(--bg-surface)', color: 'var(--text-primary)',
              border: '1px solid var(--border)', boxShadow: 'var(--shadow-float)',
              borderRadius: 'var(--radius-pill)', padding: '10px 16px',
              display: 'flex', alignItems: 'center', gap: 12,
              fontSize: 14, fontWeight: 500, zIndex: 150, whiteSpace: 'nowrap',
            }}
          >
            <span>
              {fieldTransient.kind === 'undo'
                ? cb.fieldDeletedMsg(fieldTransient.label)
                : fieldTransient.message}
            </span>
            {fieldTransient.kind === 'undo' && (
              <button
                onClick={() => { const { restore } = fieldTransient; clearFieldTransient(); restore() }}
                style={{ padding: '7px 18px', background: 'var(--accent-fill)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--text-on-accent)', fontFamily: 'inherit' }}
              >
                {dd.undoBtn}
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast / undo snackbar. The undo toast is interactive (pointerEvents
          auto); the plain confirmation toast is display-only (pointerEvents
          none). Only one is ever visible at a time: undoPending takes
          priority while it is set.
          On mobile, bottom is raised above the 60px nav + safe-area inset so the
          snackbar is never obscured. maxWidth + flexWrap prevent wide deck names from
          overflowing a narrow viewport.
          Undo toast surface: `--bg-surface` + `--border` + `--shadow-float`,
          the same floating-pill treatment as the editing-mode Done bar and
          the bulk-action bar above — not `--bg-header`. DESIGN_SYSTEM.md §7
          calls that token out by name as broken for this exact screen: in
          dark theme `--bg-header` resolves to the same value as
          `--bg-surface`, so a dark-on-dark pill with no border read as
          floating over nothing. Its action buttons carried the same
          transparency trick (`rgba(255,255,255,0.18)` on `--bg-header`) at
          `padding: '3px 12px'` — under half the tap height of every other
          pill button in this file (`ConfirmModal` and the destination sheet
          both use `9-10px 18-20px`); matched here. Appearance only — timing
          (`scheduleUndoDismiss`) and the offer itself are untouched. */}
      <AnimatePresence>
        {undoPending ? (
          <motion.div
            key="undo-toast"
            role="status"
            aria-live="polite"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
            style={{
              position: 'fixed',
              bottom: isDesktop ? 24 : 'calc(60px + env(safe-area-inset-bottom, 0px) + 12px)',
              left: '50%', transform: 'translateX(-50%)',
              background: 'var(--bg-surface)', color: 'var(--text-primary)',
              border: '1px solid var(--border)',
              padding: '10px 16px', borderRadius: 'var(--radius-pill)',
              fontSize: 14, fontWeight: 500, boxShadow: 'var(--shadow-float)',
              zIndex: 200, display: 'flex', alignItems: 'center', flexWrap: 'wrap',
              gap: 10, maxWidth: isDesktop ? undefined : 'calc(100vw - 32px)',
            }}
          >
            {undoError ? (
              <>
                <span style={{ color: 'var(--danger)' }}>{dd.undoFailed}</span>
                {/* The copy said "please retry" while offering only Dismiss.
                    That was survivable when the worst case was a card sitting
                    in the wrong deck; with a delete behind it, dismissing is
                    the reader's last chance at the cards, so the retry the
                    sentence promises has to exist. */}
                <button
                  onClick={handleUndo}
                  disabled={undoWorking}
                  style={{ padding: '9px 18px', background: 'var(--accent-fill)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 13, fontWeight: 600, cursor: undoWorking ? 'wait' : 'pointer', color: 'var(--text-on-accent)', fontFamily: 'inherit', opacity: undoWorking ? 0.6 : 1 }}
                >
                  {undoWorking ? '…' : dd.retryBtn}
                </button>
                <button
                  onClick={dismissUndo}
                  style={{ padding: '9px 18px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 13, cursor: 'pointer', color: 'var(--text-primary)', fontFamily: 'inherit' }}
                >
                  {dd.dismissBtn}
                </button>
              </>
            ) : (
              <>
                <span>
                  {/* The delete offer says what Undo will bring back, not just
                      that something can be undone — "with their content and
                      study progress" is the promise the restore then either
                      keeps or reports falling short of. */}
                  {undoPending.mode === 'delete'
                    ? (undoPending.count === undoPending.requested
                        ? dd.deletedCardsUndoable(undoPending.count)
                        : dd.deletedPartialUndoable(undoPending.count, undoPending.requested))
                    : undoPending.mode === 'move'
                      ? dd.movedCards(undoPending.count, undoPending.destDeckTitle)
                      : dd.copiedCards(undoPending.count, undoPending.destDeckTitle)}
                </span>
                <button
                  onClick={handleUndo}
                  disabled={undoWorking}
                  aria-label={undoPending.mode === 'delete'
                    ? dd.undoDeleteAria
                    : dd.undoTransferAria}
                  style={{ padding: '9px 18px', background: 'var(--accent-fill)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 13, fontWeight: 600, cursor: undoWorking ? 'wait' : 'pointer', color: 'var(--text-on-accent)', fontFamily: 'inherit', opacity: undoWorking ? 0.6 : 1 }}
                >
                  {undoWorking ? '…' : dd.undoBtn}
                </button>
              </>
            )}
          </motion.div>
        ) : toastMsg ? (
          <motion.div
            key="plain-toast"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
            style={{
              position: 'fixed',
              bottom: isDesktop ? 24 : 'calc(60px + env(safe-area-inset-bottom, 0px) + 12px)',
              left: '50%', transform: 'translateX(-50%)',
              background: 'var(--bg-header)', color: 'var(--text-on-dark)',
              padding: '10px 20px', borderRadius: 'var(--radius-pill)',
              fontSize: 14, fontWeight: 500, boxShadow: 'var(--shadow-float)',
              zIndex: 200, pointerEvents: 'none',
              maxWidth: isDesktop ? undefined : 'calc(100vw - 32px)',
              textAlign: 'center',
            }}
          >
            {toastMsg}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  )
}

// ── Confirm modal ───────────────────────────────────────────────────────────────

function ConfirmModal({ title, message, onCancel, onConfirm, loading }: { title: string; message: string; onCancel: () => void; onConfirm: () => void; loading: boolean }) {
  const { tr } = useLocale()
  const dd = tr.deckDetail
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)', padding: 32, maxWidth: 400, width: '100%', textAlign: 'center' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h3>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--text-secondary)' }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onCancel} style={{ padding: '10px 20px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 14, cursor: 'pointer', color: 'var(--text-primary)' }}>{dd.cancelLabel}</button>
          <button onClick={onConfirm} disabled={loading} style={{ padding: '10px 20px', background: 'var(--danger-fill)', color: 'var(--text-on-dark)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{loading ? dd.deletingLabel : dd.deleteLabel}</button>
        </div>
      </div>
    </div>
  )
}

// ── Skeletons ───────────────────────────────────────────────────────────────────

function DeckDetailSkeleton() {
  return (
    <div>
      <div style={{ height: 32, width: 240, background: 'var(--border)', borderRadius: 8, marginBottom: 12 }} />
      <div style={{ height: 16, width: 160, background: 'var(--border)', borderRadius: 4, marginBottom: 32 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {Array.from({ length: 5 }).map((_, i) => <div key={i} style={{ height: 52, background: 'var(--bg-surface)', borderRadius: 8, border: '1px solid var(--border)' }} />)}
      </div>
    </div>
  )
}

function CardsListSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Array.from({ length: 6 }).map((_, i) => <div key={i} style={{ height: 52, background: 'var(--bg-surface)', borderRadius: 8, border: '1px solid var(--border)' }} />)}
    </div>
  )
}

// ── Learning toggle switch ────────────────────────────────────────────────────────

// ── Deck picker modal ─────────────────────────────────────────────────────────────

function DeckPickerModal({ mode, sourceDeckId, selection, selectionCount, onClose, onSuccess }: {
  mode: 'move' | 'copy'
  sourceDeckId: string
  /** The selection as the server understands it — a scope, not a list of the
   * ids this client had loaded (ADR-067 Decision 6, brief §23 item 9). */
  selection: CardSelectionRequest
  /** Its size, or `null` when not yet known. The heading says which cards are
   * about to move; when the count is unknown it says so rather than printing a
   * loaded-set stand-in, and the confirm button waits. */
  selectionCount: number | null
  onClose: () => void
  onSuccess: (result: {
    count: number
    targetDeckId: string
    targetDeckTitle: string
    resultCardIds: string[]
    /** Where the moved cards sat in the source deck, captured server-side.
     * Empty for a copy, which does not disturb the source. */
    originalOrder: { id: string; afterId: string | null }[]
    /** Copy only: the server's handle on the cards THIS copy created, which is
     * the only thing that can undo it correctly. Null for a move. */
    undoToken: string | null
  }) => void
}) {
  const { tr } = useLocale()
  const dd = tr.deckDetail
  const [selectedId, setSelectedId] = useState('')
  const [newTitle, setNewTitle] = useState('')
  const [search, setSearch] = useState('')
  // page_size capped at 100 by the backend (PageParams le=100); covers most users.
  // If a user could own >100 eligible decks, server-side search would be needed.
  const { data: decksData, isLoading: decksLoading, isError: decksError } = useDecks(1, 100)
  const createDeck = useCreateDeck()
  const moveCards = useMoveCards()
  const copyCards = useCopyCards()
  // Identifies the copy this modal is performing. Minted on the first confirm
  // and kept until one succeeds, so pressing the button again after a failure
  // is a RETRY of that copy (the server recognises the id and does not
  // duplicate) while a later, separate copy — a fresh open of this modal —
  // carries a new id and genuinely produces another set of cards. Reusing a
  // single id forever is what the old server-side key amounted to, and it made
  // every repeat copy a no-op.
  const operationIdRef = useRef<string | null>(null)

  const allDecks = decksData?.items ?? []
  const filterable = allDecks.filter((d) => d.id !== sourceDeckId && !d.is_system)
  const decks = search
    ? filterable.filter((d) => d.title.toLowerCase().includes(search.toLowerCase()))
    : filterable

  const isNew = selectedId === '__new__'
  // An unknown count blocks confirmation: it means the deck's size has not been
  // read, and a bulk action must not run on a scope nobody could state.
  const canConfirm = (isNew ? newTitle.trim().length > 0 : selectedId !== '')
    && selectionCount !== null
  const isPending = createDeck.isPending || moveCards.isPending || copyCards.isPending
  const hasError = moveCards.isError || copyCards.isError || createDeck.isError

  async function handleConfirm() {
    try {
      let targetId = selectedId
      let targetTitle = isNew ? newTitle.trim() : (allDecks.find((d) => d.id === selectedId)?.title ?? '')
      if (isNew) {
        const deck = await createDeck.mutateAsync({ title: newTitle.trim() })
        targetId = deck.id
        targetTitle = deck.title
      }
      if (mode === 'move') {
        const result = await moveCards.mutateAsync({ sourceDeckId, targetDeckId: targetId, selection })
        onSuccess({
          count: result.moved,
          targetDeckId: targetId,
          targetDeckTitle: targetTitle,
          resultCardIds: result.card_ids,
          originalOrder: result.original_order.map((o) => ({ id: o.id, afterId: o.after_id })),
          undoToken: null,
        })
      } else {
        if (operationIdRef.current === null) operationIdRef.current = crypto.randomUUID()
        const result = await copyCards.mutateAsync({
          sourceDeckId, targetDeckId: targetId, selection,
          operationId: operationIdRef.current,
        })
        onSuccess({
          count: result.copied,
          targetDeckId: targetId,
          targetDeckTitle: targetTitle,
          resultCardIds: result.card_ids,
          originalOrder: [],
          undoToken: result.undo_token,
        })
      }
      onClose()
    } catch {
      // error surfaced by hasError above
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)', padding: 28, maxWidth: 440, width: '100%' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
          {selectionCount === null
            ? dd.bulkCountUnknown
            : mode === 'move' ? dd.moveTo(selectionCount) : dd.copyTo(selectionCount)}
        </h3>
        {/* What happens to THIS deck, said plainly. The two modes sit side by
            side in both action bars and differ in exactly one respect — whether
            the cards leave — and the heading's verb is the only thing carrying
            that. This is the last point before it happens. */}
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
          {mode === 'move' ? dd.moveConsequence : dd.copyConsequence}
        </p>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={dd.searchDecksPlaceholder}
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 14, marginBottom: 10, outline: 'none', display: 'block' }}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto', marginBottom: 16 }}>
          {/* New deck option — hidden while searching */}
          {!search && (
            <>
              <DeckOption label={dd.newDeckOption} secondary="" selected={isNew} onClick={() => setSelectedId('__new__')} bold />
              {isNew && (
                <input
                  autoFocus
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && canConfirm && !isPending) handleConfirm() }}
                  placeholder={dd.newDeckPlaceholder}
                  style={{ marginLeft: 16, padding: '7px 12px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--accent)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 14, outline: 'none' }}
                />
              )}
            </>
          )}

          {decks.map((d) => (
            <DeckOption
              key={d.id}
              label={d.title}
              secondary={dd.cardCount(d.card_count)}
              selected={selectedId === d.id}
              onClick={() => setSelectedId(d.id)}
            />
          ))}

          {decksError && (
            <p style={{ fontSize: 13, color: 'var(--danger)', padding: '8px 0', textAlign: 'center', margin: 0 }}>{dd.loadDecksError}</p>
          )}
          {!decksLoading && !decksError && decks.length === 0 && search && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '8px 0', textAlign: 'center', margin: 0 }}>{dd.noDecksFound}</p>
          )}
          {!decksLoading && !decksError && filterable.length === 0 && !search && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '8px 14px', margin: 0 }}>{dd.noOtherDecks}</p>
          )}
        </div>

        {hasError && (
          <p style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 12, margin: '0 0 12px' }}>{dd.transferError}</p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={isPending} style={{ padding: '9px 18px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 14, cursor: 'pointer', color: 'var(--text-primary)' }}>{dd.cancelLabel}</button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || isPending}
            style={{ padding: '9px 18px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: canConfirm && !isPending ? 'pointer' : 'not-allowed', opacity: !canConfirm || isPending ? 0.6 : 1 }}
          >
            {isPending ? (mode === 'move' ? dd.moving : dd.copying) : mode === 'move' ? dd.moveBtn : dd.copyBtn}
          </button>
        </div>
      </div>
    </div>
  )
}

function DeckOption({ label, secondary, selected, onClick, bold = false }: {
  label: string; secondary: string; selected: boolean; onClick: () => void; bold?: boolean
}) {
  return (
    <button
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left', padding: '10px 14px', borderRadius: 'var(--radius-sm)', border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--border)'}`, background: selected ? 'var(--accent-soft)' : 'transparent', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 14, fontWeight: bold ? 600 : 400, width: '100%' }}
    >
      <span>{label}</span>
      {secondary && <span style={{ fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0, marginLeft: 8 }}>{secondary}</span>}
    </button>
  )
}

// ── Desktop screen-header overflow menu ──────────────────────────────────────────
// The desktop counterpart of the mobile ⋯ menu above, hosted in the
// screen-header bar's tools zone instead of the page body (never both — see
// the `!isDesktop` gate on the mobile version above). Deliberately narrower
// than the mobile menu: system decks never reach this component at all
// (headerShowOverflow excludes them), so there is no "copy to personal
// library" item here — that action is already reachable from Catalog/Explore
// before a system deck is ever opened, so dropping it here is not a loss of
// the feature, just of a second path to it.
/** The ⋯ menu's inputs, gathered so the header can hand them through. */
interface DeckHeaderMenuProps {
  open: boolean
  onToggle: () => void
  onClose: () => void
  notFilledCount: number
  cardsTotal: number | undefined
  canReorder: boolean
  filterActive: boolean
  exporting: boolean
  updateDeckPending: boolean
  onQuickPrep: () => void
  onResetMastered: () => void
  onEditDetails: () => void
  onFilterCards: () => void
  onFillEmptyCards: () => void
  onReorderCards: () => void
  onAddFromText: () => void
  onImportCsv: () => void
  onExportCsv: () => void
  onTogglePublic: () => void
  onCopyShareLink: () => void
  onDelete: () => void
  onStudyCards: () => void
}

interface DeckHeaderSelectionProps {
  active: boolean
  count: number | null
  selectableTotal: number | null
  wholeDeck: boolean
  countsUnavailable: boolean
  onSelectAll: () => void
  onDeselectAll: () => void
  /** A running or failed Fill over the selection — outranks the buttons, the
   *  same three-state rule the phone's selection bar follows. */
  fillStatus: { tone: 'error' | 'muted'; text: string } | null
  onFill: () => void
  onMove: () => void
  onCopy: () => void
  onDelete: () => void
}

/**
 * The desktop deck screen's content for the screen-header bar (ADR-067,
 * thirteenth amendment). Everything deck-level lives here now — identity and
 * its facts, the daily-learning switch, the two study exercises, the edit
 * mode (or, on a system deck, Copy), the ⋯ menu — so the two columns start
 * directly under the bar and nothing deck-level sits in flow above them.
 *
 * **The fit ladder (what gives first).** The bar never wraps. As the row
 * tightens, `ScreenHeaderBar` steps down one rung at a time:
 *
 *   1. *Study with flashcards* sheds its label (icon + hint);
 *   2. the daily-learning switch sheds its label (switch + hint);
 *   3. the back link sheds its label (chevron, name kept as aria-label);
 *   4. with nothing left to give, the name and its facts line truncate.
 *
 * *Study by memory*, the edit mode's capsule (or Copy) and ⋯ never degrade.
 * Below the two-pane threshold (`narrow`, where the pane becomes a sheet)
 * *Study with flashcards* is not in the bar at all — it is the first item of
 * ⋯ — and the switch is icon-only from the start: it reports the deck's state,
 * and a state behind a menu cannot be read at a glance, so it is the one thing
 * that stays visible however narrow the bar gets.
 *
 * **Selection takes the bar over.** While cards are selected the bar is the
 * selection header — ✕ Cancel in the back slot, the count as the title,
 * *Select all* and the bulk actions on the right — exactly as the phone's
 * `SelectModeHeader` takes over `DeckTopStrip`'s slot. The floating pill this
 * replaces sat over the last rows of a long deck.
 *
 * Handlers are read through a ref so the published config changes identity
 * only when what the bar SHOWS changes — the bar re-measures its fit on every
 * new config, and handler identities change on every page render.
 */
function DeckDesktopHeader(props: {
  deck: Deck
  backLabel: string
  onBack: () => void
  isOwner: boolean
  canEditCards: boolean
  deckEditMode: boolean
  onSetDeckEditMode: (on: boolean) => void
  hasCards: boolean
  narrow: boolean
  dailyPending: boolean
  onToggleDaily: () => void
  onStudyMemory: () => void
  onStudyCards: () => void
  copyPending: boolean
  onCopyToLibrary: () => void
  menu: DeckHeaderMenuProps
  selection: DeckHeaderSelectionProps
}) {
  const { tr } = useLocale()
  const dd = tr.deckDetail
  const dm = tr.deckManagement
  const dr = tr.deckReorder
  const latest = useRef(props)
  useLayoutEffect(() => { latest.current = props })

  const { deck, backLabel, isOwner, canEditCards, deckEditMode, hasCards, narrow, dailyPending, copyPending, menu, selection } = props
  const showQuickPrep = deck.learned_count > 0
  const showEditGroups = !deck.is_system && (isOwner || !!deck.share_token)
  // ⋯ also carries Study with flashcards below the two-pane threshold.
  const showOverflow = showQuickPrep || showEditGroups || narrow
  const learnedPct = deck.card_count > 0 ? Math.round((deck.learned_count / deck.card_count) * 100) : null
  // Primitives, so the memo below keys on what the bar shows rather than on
  // an object the page rebuilds every render.
  const fillTone = selection.fillStatus?.tone ?? null
  const fillText = selection.fillStatus?.text ?? null

  const config = useMemo((): ScreenHeaderConfig => {
    const h = () => latest.current
    if (selection.active) {
      const selectAllLabel = selection.wholeDeck
        ? dd.deselectAll
        : selection.selectableTotal !== null ? dd.selectAll(selection.selectableTotal) : dd.selectAllPending
      return {
        back: { label: dd.cancelLabel, icon: 'close', onClick: () => h().selection.onDeselectAll() },
        title: selection.count === null ? dd.nSelectedPending : dd.nSelected(selection.count),
        subtitle: selection.countsUnavailable ? dd.countUnavailable : null,
        lead: {
          full: (
            <HeaderCapsule
              label={selectAllLabel}
              onClick={() => (h().selection.wholeDeck ? h().selection.onDeselectAll() : h().selection.onSelectAll())}
            />
          ),
        },
        tools: isOwner ? {
          full: fillText !== null ? (
            <span role="status" style={{ fontSize: 12.5, fontFamily: 'var(--font-ui)', color: fillTone === 'error' ? 'var(--text-header)' : 'var(--text-header-secondary)', whiteSpace: 'nowrap' }}>
              {fillText}
            </span>
          ) : (
            <>
              <HeaderCapsule label={dm.fillSelectedBtn} onClick={() => h().selection.onFill()} />
              <HeaderCapsule label={dd.moveToBtn} variant="solid" onClick={() => h().selection.onMove()} />
              <HeaderCapsule label={dd.copyToBtn} onClick={() => h().selection.onCopy()} />
              <HeaderCapsule label={dd.deleteBtn} onClick={() => h().selection.onDelete()} />
            </>
          ),
        } : null,
      }
    }

    // The deck's facts, on the line under its name: what the band's meta line
    // said, in the same words. Due keeps its warning colour; it is the one
    // fact here that asks for something today.
    // Below the two-pane threshold a system deck says so at the head of this
    // line instead of in the chip beside its name: at 768–850px the chip's
    // ~78px was the name's whole width once every rung was spent.
    const facts: React.ReactNode[] = []
    if (deck.is_system && narrow) facts.push(dd.systemBadge)
    facts.push(dd.cardCount(deck.card_count))
    if (deck.due_count !== null && deck.due_count > 0) {
      facts.push(<span key="due" style={{ color: 'var(--warning)', fontWeight: 600 }}>{dd.dueBadge(deck.due_count)}</span>)
    }
    if (learnedPct !== null) facts.push(dd.learnedPct(learnedPct))
    if (deck.cefr_level) facts.push(deck.cefr_level)
    facts.push(getLangMeta(deck.language).label)
    if (deck.is_public && !deck.is_system) facts.push(dd.publicBadge)
    if (!isOwner && !deck.is_system) facts.push(dd.readOnlyBadge)
    const subtitle = facts.map((f, i) => (
      <Fragment key={i}>{i > 0 && <span aria-hidden> · </span>}{f}</Fragment>
    ))

    const dailySwitch = (showLabel: boolean) => (
      <DeckDailySwitch
        active={deck.is_active_for_learning}
        disabled={dailyPending}
        label={dd.inDailyLearning}
        hint={dd.inDailyLearningDesc}
        showLabel={showLabel}
        onToggle={() => h().onToggleDaily()}
      />
    )
    const studyCards = (iconOnly: boolean) => (
      <HeaderCapsule label={dd.learnByCards} icon={<Layers size={14} strokeWidth={2.2} />} iconOnly={iconOnly} onClick={() => h().onStudyCards()} />
    )
    // The screen's primary action: the header plane's solid ramp, never the
    // accent (DESIGN_SYSTEM.md §10.5 — the accent fill stays out of the bar).
    const studyMemory = (
      <HeaderCapsule label={dd.learnByMemory} variant="solid" onClick={() => h().onStudyMemory()} />
    )
    const lead = narrow
      ? { full: <>{dailySwitch(false)}{studyMemory}</> }
      : {
          full: <>{dailySwitch(true)}{studyCards(false)}{studyMemory}</>,
          compact: <>{dailySwitch(true)}{studyCards(true)}{studyMemory}</>,
          overflow: <>{dailySwitch(false)}{studyCards(true)}{studyMemory}</>,
        }

    // The action at its icon — offered below the two-pane threshold only, and
    // taken by the bar only once the name is down to its narrow floor (the
    // design owner's decision, 2026-09-23: at 768–850px with the sidebar
    // open the name otherwise reached 0px and ⋯ left the bar). Same capsule
    // weight; the label becomes the hint and the accessible name.
    const actionCompact = !narrow ? null : canEditCards ? (
      deckEditMode ? (
        <DeckEditModeBadge label={dd.deckEditModeOn} offLabel={dd.deckEditModeOff} offAria={dd.deckEditModeOffAria} onOff={() => h().onSetDeckEditMode(false)} iconOnly />
      ) : (
        <HeaderCapsule label={dd.menuEditAllCards} ariaLabel={dd.menuEditAllCardsAria} icon={<SquarePen size={13} strokeWidth={2.2} />} iconOnly disabled={!hasCards} onClick={() => h().onSetDeckEditMode(true)} />
      )
    ) : deck.is_system ? (
      <HeaderCapsule label={dd.barCopyToLibrary} ariaLabel={dd.menuCopyToLibrary} icon={<Copy size={13} strokeWidth={2.2} />} iconOnly disabled={copyPending} onClick={() => h().onCopyToLibrary()} />
    ) : null

    const action = canEditCards ? (
      deckEditMode ? (
        <DeckEditModeBadge label={dd.deckEditModeOn} offLabel={dd.deckEditModeOff} offAria={dd.deckEditModeOffAria} onOff={() => h().onSetDeckEditMode(false)} />
      ) : (
        <DeckEditModeEntryButton label={dd.menuEditAllCards} aria={dd.menuEditAllCardsAria} disabled={!hasCards} onClick={() => h().onSetDeckEditMode(true)} />
      )
    ) : deck.is_system ? (
      // The system deck's own action: take a copy to edit (ADR-008 — the deck
      // itself is written by seeders only). It was reachable only from the
      // phone's menu; the desktop had no way to it at all.
      <HeaderCapsule
        label={copyPending ? dd.menuCopying : dd.barCopyToLibrary}
        ariaLabel={dd.menuCopyToLibrary}
        icon={<Copy size={13} strokeWidth={2.2} />}
        disabled={copyPending}
        onClick={() => h().onCopyToLibrary()}
      />
    ) : null

    return {
      back: { label: backLabel, onClick: () => h().onBack() },
      title: deck.title,
      titleHint: deck.description ? `${deck.title}\n\n${deck.description}` : deck.title,
      subtitle,
      chip: deck.is_system && !narrow ? dd.systemBadge : null,
      lead,
      action,
      actionCompact,
      tools: showOverflow ? {
        full: (
          <DeckHeaderOverflowMenu
            deck={deck}
            isOwner={isOwner}
            open={menu.open}
            onToggle={() => h().menu.onToggle()}
            onClose={() => h().menu.onClose()}
            dd={dd} dm={dm} dr={dr}
            showQuickPrep={showQuickPrep}
            showEditGroups={showEditGroups}
            notFilledCount={menu.notFilledCount}
            cardsTotal={menu.cardsTotal}
            hasCards={hasCards}
            canReorder={menu.canReorder}
            filterActive={menu.filterActive}
            exporting={menu.exporting}
            updateDeckPending={menu.updateDeckPending}
            onStudyCards={narrow ? () => h().menu.onStudyCards() : undefined}
            onQuickPrep={() => h().menu.onQuickPrep()}
            onResetMastered={() => h().menu.onResetMastered()}
            onEditDetails={() => h().menu.onEditDetails()}
            onFilterCards={() => h().menu.onFilterCards()}
            onFillEmptyCards={() => h().menu.onFillEmptyCards()}
            onReorderCards={() => h().menu.onReorderCards()}
            onAddFromText={() => h().menu.onAddFromText()}
            onImportCsv={() => h().menu.onImportCsv()}
            onExportCsv={() => h().menu.onExportCsv()}
            onTogglePublic={() => h().menu.onTogglePublic()}
            onCopyShareLink={() => h().menu.onCopyShareLink()}
            onDelete={() => h().menu.onDelete()}
          />
        ),
      } : null,
    }
  }, [
    deck, backLabel, isOwner, canEditCards, deckEditMode, hasCards, narrow, dailyPending, copyPending,
    showQuickPrep, showEditGroups, showOverflow, learnedPct, dd, dm, dr,
    menu.open, menu.notFilledCount, menu.cardsTotal, menu.canReorder, menu.filterActive, menu.exporting, menu.updateDeckPending,
    selection.active, selection.count, selection.selectableTotal, selection.wholeDeck, selection.countsUnavailable,
    fillTone, fillText,
  ])

  useScreenHeader(config)
  return null
}

function DeckHeaderOverflowMenu({
  deck, isOwner, open, onToggle, onClose, dd, dm, dr,
  showQuickPrep, showEditGroups,
  notFilledCount, cardsTotal, hasCards, canReorder, filterActive,
  exporting, updateDeckPending,
  onStudyCards, onQuickPrep, onResetMastered, onEditDetails, onFilterCards, onFillEmptyCards, onReorderCards,
  onAddFromText, onImportCsv, onExportCsv, onTogglePublic, onCopyShareLink, onDelete,
}: {
  deck: Deck
  isOwner: boolean
  open: boolean
  onToggle: () => void
  onClose: () => void
  dd: ReturnType<typeof useLocale>['tr']['deckDetail']
  dm: ReturnType<typeof useLocale>['tr']['deckManagement']
  dr: ReturnType<typeof useLocale>['tr']['deckReorder']
  /** Study history exists (deck.learned_count > 0) — shows Quick prep,
   *  independent of ownership/is_system. */
  showQuickPrep: boolean
  /** Non-system AND (owner or has a share token) — everything else in this
   *  menu, all of it either an edit or a sharing action on the deck. */
  showEditGroups: boolean
  notFilledCount: number
  cardsTotal: number | undefined
  hasCards: boolean
  canReorder: boolean
  filterActive: boolean
  exporting: boolean
  updateDeckPending: boolean
  /** Present only below the two-pane threshold, where *Study with
   *  flashcards* folds out of the bar and into this menu. */
  onStudyCards?: () => void
  onQuickPrep: () => void
  onResetMastered: () => void
  onEditDetails: () => void
  onFilterCards: () => void
  onFillEmptyCards: () => void
  onReorderCards: () => void
  onAddFromText: () => void
  onImportCsv: () => void
  onExportCsv: () => void
  onTogglePublic: () => void
  onCopyShareLink: () => void
  onDelete: () => void
}) {
  const mr = useLocale().tr.masteryReset
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={onToggle}
        aria-label={dd.moreOptionsLabel}
        aria-expanded={open}
        aria-haspopup="menu"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-header-secondary)', padding: 8, borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 36, minHeight: 36,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-header)' }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-header-secondary)' }}
      >
        <MoreHorizontal size={18} />
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={onClose} />
          <div
            role="menu"
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100,
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)',
              minWidth: 240,
              // A menu taller than the space beneath its trigger must scroll
              // within that space rather than clip it — `overflow: 'hidden'`
              // used to just cut off whatever fell past the viewport's bottom
              // edge (observed: Delete deck, always the last item, unreachable
              // at full screen height with no way out but shrinking the
              // window). The ceiling is `--header-height` (this menu is
              // anchored to the header bar) plus the same 6px gap the menu
              // opens with below its trigger; `overflowY: 'auto'` is what the
              // mobile sheet variant of this same menu already does (see the
              // `!isDesktop` branch above) — this brings the desktop one to
              // parity rather than inventing a second mechanism.
              maxHeight: 'calc(100vh - var(--header-height) - 12px)',
              overflowY: 'auto',
            }}
          >
            {/* Quick prep — a study entrance, not an edit, so it's the one
                thing gated on study history alone rather than on
                showEditGroups: a system-deck viewer (never an owner) still
                gets it once the deck has one, and gains nothing else here. */}
            {(showQuickPrep || onStudyCards) && (
              <OverflowMenuGroup label={dd.menuStudyGroup}>
                {/* Folded here from the bar below the two-pane threshold —
                    the one control that leaves the bar there. */}
                {onStudyCards && (
                  <OverflowMenuItem label={dd.learnByCards} onClick={onStudyCards} />
                )}
                {showQuickPrep && (
                  <OverflowMenuItem label={dd.quickPrep} sublabel={dd.quickPrepDesc} onClick={onQuickPrep} />
                )}
                {/* Same gate as Quick prep: a deck with study history. The
                    confirm reads the exact mastered count before anything
                    can be committed (ADR-098). */}
                {showQuickPrep && (
                  <OverflowMenuItem label={mr.deckMenuItem} sublabel={mr.deckMenuItemSub} onClick={onResetMastered} />
                )}
              </OverflowMenuGroup>
            )}

            {showEditGroups && isOwner && (
              <OverflowMenuGroup label={dd.menuDeckGroup}>
                <OverflowMenuItem label={dd.menuEditDetails} sublabel={dd.menuEditDetailsSub} onClick={onEditDetails} />
              </OverflowMenuGroup>
            )}

            {showEditGroups && isOwner && (
              <OverflowMenuGroup label={dm.menuManageGroup}>
                <OverflowMenuItem label={dm.menuFilterCards} onClick={onFilterCards} disabled={!hasCards} />
                <OverflowMenuItem
                  label={dm.menuFillEmptyCards}
                  sublabel={notFilledCount > 0 && cardsTotal ? dm.resultCount(notFilledCount, cardsTotal) : undefined}
                  onClick={onFillEmptyCards}
                  disabled={!hasCards}
                />
                <OverflowMenuItem
                  label={dr.menuReorderCards}
                  sublabel={filterActive ? dr.menuReorderFiltered : dr.menuReorderSub}
                  onClick={onReorderCards}
                  disabled={!canReorder}
                />
              </OverflowMenuGroup>
            )}

            {showEditGroups && isOwner && (
              <OverflowMenuGroup label={dd.menuContentGroup}>
                <OverflowMenuItem label={dd.menuAddFromText} onClick={onAddFromText} />
                <OverflowMenuItem label={dd.menuImportCsv} onClick={onImportCsv} />
                <OverflowMenuItem label={exporting ? dd.menuExporting : dd.menuExportCsv} onClick={onExportCsv} disabled={exporting} />
              </OverflowMenuGroup>
            )}

            {showEditGroups && (isOwner || deck.share_token) && (
              <OverflowMenuGroup label={dd.menuSharingGroup}>
                {isOwner && (
                  <OverflowMenuItem
                    label={deck.is_public ? dd.menuMakePrivate : dd.menuMakePublic}
                    sublabel={deck.is_public ? dd.menuMakePrivateSub : dd.menuMakePublicSub}
                    onClick={onTogglePublic}
                    disabled={updateDeckPending}
                  />
                )}
                {deck.share_token && (
                  <OverflowMenuItem label={dd.menuCopyShareLink} onClick={onCopyShareLink} />
                )}
              </OverflowMenuGroup>
            )}

            {showEditGroups && isOwner && !deck.is_inbox && (
              <>
                <OverflowMenuSeparator spacious />
                <OverflowMenuItem label={dd.menuDeleteDeck} onClick={onDelete} destructive />
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * The deck-editing mode's ENTRANCE (ADR-067 amendment; widened 2026-09-09).
 * Published into the screen-header bar's `action` slot — the first half of
 * the controls group at the bar's right END, not beside the deck's name —
 * whenever the mode is off. `DeckEditModeBadge` below takes the same slot
 * once it is on, so the two never render together.
 *
 * This used to live only in the deck's overflow menu, third inside a Manage
 * group, level with Filter cards and Fill empty cards — operations that
 * change nothing about how the screen behaves once they finish. Entering
 * this mode changes how every card on the screen opens for as long as it is
 * on, which is a different kind of weight, and a menu buried three deep
 * cannot show it. The overflow menu's entry is retired outright rather than
 * kept alongside this one: this control is always visible beside the deck's
 * name whenever the deck can be edited at all, so a second entrance hidden
 * in a menu would be strictly worse than this one — never found first, only
 * found by a reader who already knew to look.
 *
 * **Revised 2026-09-10 — a SOLID capsule on the header plane's own ramp,
 * same weight on and off.** This is the third treatment this control has
 * worn, and the first two each failed the same underlying token collision,
 * so the collision is recorded here rather than the treatment alone:
 *
 *  1. `--control-solid` fill (DESIGN_SYSTEM.md §7's rule for a solid control
 *     on a chrome plane). In LIGHT theme `--control-solid` is `#2D3436` and
 *     `--bg-screen-header` is also `#2D3436` — the same literal. The fill
 *     painted at 1.00:1 against the bar and the control vanished.
 *  2. A transparent capsule with a `--text-header` hairline (2026-09-09),
 *     adopted to escape that collision. It escapes the fill collision by
 *     having no fill at all — so the control's plane IS the bar's plane, in
 *     both themes, and it reads as a line of text with an outline rather
 *     than as a control. That is the same defect wearing different clothes.
 *
 *  3. A SOLID capsule on the header plane's own ramp
 *     (`--control-solid-header`, 2026-09-10). It escapes both collisions —
 *     it has a plane, and the plane is not the bar's — and it is what §8
 *     prescribes for a control that needs maximum contrast here.
 *
 * **Superseded 2026-09-12 — a BORDERED capsule on `--header-control-plane`
 * (§10.5/§10.6).** Treatment 3 was right about the collision and wrong about
 * the weight. The bar's action is never the screen's loudest control: the one
 * filled control this screen carries is Study, in the page body. And a solid
 * capsule cannot serve both states of a mode at one weight — solid ON against
 * anything quieter OFF makes the off switch the fainter of the two, and the
 * off switch is the state a reader has to hunt for. Nobody hunts for a button
 * they have already pressed; they hunt for the way out.
 *
 * So the capsule is `--header-control-plane` (a white alpha — it HAS a plane,
 * which is what treatment 2 lacked) with a `--header-control-border` edge and
 * `--header-control-plane-hover` on hover. It reads as pressable on the bar
 * without claiming to be the screen's primary action. `--control-solid-header`
 * is not deleted: it is the recorded answer to a collision that has now
 * recurred twice, and it stands ready for a control on this bar that genuinely
 * needs maximum contrast. This one does not.
 *
 * Still never `--accent`, and never `--control-solid`: the first belongs to
 * the study action in the page body, and the second is the MOBILE chrome
 * plane's ramp, which is this bar's own literal in light theme.
 *
 * Both states continue to share ONE capsule style
 * (`DECK_EDIT_MODE_CAPSULE_STYLE` below), which is what keeps their weight
 * matched; that was the 2026-09-09 pass's real goal and it survives intact.
 * What tells the two states apart is the wording and the appearance of a
 * second segment — Done, divided off inside the same capsule by
 * `--header-control-divider` — never the capsule's solidity.
 */
function DeckEditModeEntryButton({ label, aria, disabled, onClick }: {
  label: string
  aria: string
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={aria}
      style={{ ...DECK_EDIT_MODE_CAPSULE_STYLE, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'default' : 'pointer' }}
      onMouseEnter={(e) => {
        if (disabled) return
        const el = e.currentTarget as HTMLElement
        el.style.background = 'var(--header-control-plane-hover)'
        el.style.borderColor = 'var(--header-control-border-hover)'
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement
        el.style.background = 'var(--header-control-plane)'
        el.style.borderColor = 'var(--header-control-border)'
      }}
    >
      <SquarePen size={13} strokeWidth={2.2} />
      {label}
    </button>
  )
}

/** Shared by both states of the deck-editing mode control (see the entrance's
 * own comment above for why they must match, and for the token collision that
 * produced the two treatments before this one): a solid capsule on the
 * SCREEN-HEADER plane's own ramp. Never `--control-solid` (that is the mobile
 * chrome plane's ramp, and it is the bar's own literal in light theme) and
 * never `--accent`.
 *
 * One height for anything sitting in this bar (`--header-control-h`, 32), so
 * the action and the overflow share a baseline rather than each being as tall
 * as its own padding makes it. The border is declared in both states (it is
 * the capsule's edge now, not a spacer) and the box is `border-box`, so the ON
 * state's extra segment cannot change the capsule's height. */
const DECK_EDIT_MODE_CAPSULE_STYLE: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
  height: 'var(--header-control-h)', boxSizing: 'border-box',
  background: 'var(--header-control-plane)',
  border: '1px solid var(--header-control-border)',
  borderRadius: 'var(--radius-pill)', padding: '0 12px',
  color: 'var(--text-header)', fontFamily: 'var(--font-ui)',
  fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
}

/**
 * The deck-editing mode's ACTIVE indicator, carrying its own off switch
 * (ADR-067 amendment). Published into the same `action` slot as the
 * entrance above, once the mode is on.
 *
 * One element, not two: the label says which mode is on and the button beside
 * it ends that mode, so a reader who notices the state has already found the
 * way out of it.
 *
 * **Revised 2026-09-10 — the same SOLID capsule the entrance uses** (see
 * that function's comment for the token collision behind the change). What
 * marks this as the ON state is the label text and the Done switch's own
 * appearance: a small pill INSIDE the capsule, painted in the capsule's own
 * two colors reversed — `--control-solid-header-text` as fill against
 * `--control-solid-header` as text. It reads as a nested action rather than
 * a second control because it sits inside the capsule's own box rather than
 * beside it, and it inverts against the capsule for the same reason the
 * capsule inverts against the bar: a control's separation comes from its own
 * plane's ramp, never from the plane it happens to be sitting on.
 */
function DeckEditModeBadge({ label, offLabel, offAria, onOff, iconOnly = false }: {
  label: string
  offLabel: string
  offAria: string
  onOff: () => void
  /** The bar's last fit rung: the mode's name gives way to its icon (kept as
   *  the group's accessible name and its title); *Done* keeps its word, since
   *  the off switch is the state a reader has to find (§10.6). */
  iconOnly?: boolean
}) {
  // The same capsule the entrance wears, at the same weight, with a second
  // segment DIVIDED OFF inside it rather than a second control beside it
  // (§10.6): one object, two segments, so the mode and its way out cannot be
  // read as two separate things — and so the ON state cannot out-weigh the
  // OFF state. The divider is a border on the segment, not a gap: a gap would
  // let the two halves drift apart under a longer label.
  return (
    <div
      role="group"
      aria-label={label}
      title={iconOnly ? label : undefined}
      style={{ ...DECK_EDIT_MODE_CAPSULE_STYLE, gap: 0, padding: '0 0 0 12px' }}
    >
      <SquarePen size={13} strokeWidth={2.2} style={{ flexShrink: 0, marginRight: iconOnly ? 12 : 0 }} />
      {!iconOnly && (
        <span style={{ margin: '0 12px 0 6px', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      )}
      <button
        onClick={onOff}
        aria-label={offAria}
        style={{
          alignSelf: 'stretch', display: 'flex', alignItems: 'center',
          background: 'none', color: 'var(--text-header)',
          border: 'none', borderLeft: '1px solid var(--header-control-divider)',
          borderRadius: 0, padding: '0 12px', cursor: 'pointer',
          fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--header-control-hover)' }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'none' }}
      >
        {offLabel}
      </button>
    </div>
  )
}

// ── Overflow menu primitives (D5) ────────────────────────────────────────────────

function OverflowMenuItem({ label, sublabel, onClick, disabled = false, destructive = false }: {
  label: string
  sublabel?: string
  onClick: () => void
  disabled?: boolean
  destructive?: boolean
}) {
  return (
    <button
      role="menuitem"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
        width: '100%', textAlign: 'left',
        padding: '11px 16px', background: 'none', border: 'none',
        fontSize: 14, cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'var(--text-secondary)' : destructive ? 'var(--danger)' : 'var(--text-primary)',
        opacity: disabled ? 0.5 : 1,
        minHeight: 44,
        transition: 'background 0.12s',
        gap: sublabel ? 2 : 0,
      }}
      onMouseEnter={(e) => { if (!disabled) (e.currentTarget as HTMLElement).style.background = 'var(--bg-secondary)' }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'none' }}
    >
      <span style={{ lineHeight: 1.3 }}>{label}</span>
      {sublabel && <span style={{ fontSize: 11, color: destructive ? 'inherit' : 'var(--text-secondary)', opacity: 0.75, lineHeight: 1.3 }}>{sublabel}</span>}
    </button>
  )
}

function OverflowMenuSeparator({ spacious = false }: { spacious?: boolean }) {
  return <div role="separator" style={{ height: 1, background: 'var(--border)', margin: spacious ? '8px 0' : '4px 0' }} />
}

function OverflowMenuGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{
        padding: '10px 16px 2px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.07em',
        textTransform: 'uppercase' as const,
        color: 'var(--text-secondary)',
        userSelect: 'none',
      }}>
        {label}
      </div>
      {children}
      <OverflowMenuSeparator />
    </div>
  )
}

// ── Back-navigation helpers ───────────────────────────────────────────────────────

function deckBackDest(from: string | undefined, isSystem: boolean): string {
  if (from) return from
  return isSystem ? '/catalog' : '/decks'
}

function deckBackLabel(from: string | undefined, isSystem: boolean, labelMyDecks: string, labelCatalog: string, labelMyCourses: string): string {
  if (from) {
    const params = new URLSearchParams(from.split('?')[1] ?? '')
    const seg = params.get('seg')
    if (from.startsWith('/library')) {
      if (seg === 'catalog') return labelCatalog
      return params.get('sub') === 'courses' ? labelMyCourses : labelMyDecks
    }
    if (from.startsWith('/courses')) return labelMyCourses
    if (from.startsWith('/catalog')) return labelCatalog
    if (from.startsWith('/decks')) return labelMyDecks
  }
  return isSystem ? labelCatalog : labelMyDecks
}

// ── Styles ───────────────────────────────────────────────────────────────────────

const primaryBtnStyle: React.CSSProperties = { padding: '10px 20px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtnStyle: React.CSSProperties = { padding: '10px 20px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }
